import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Crown,
  Loader2,
  Palette,
  PencilRuler,
  Sparkles,
  Star,
} from "lucide-react";
import {
  generateAiCompositionPlan,
  generateAiTemplatePlan,
  refineAiCompositionPlan,
} from "../api/adminAiTemplates";
import { createAssetLibraryItem, getAssetLibraryItems } from "../api/assetLibrary";
import { useAuth } from "../contexts/AuthContext";
import { getTemplateAssetPlan } from "../data/templateAssetRegistry";
import {
  buildDefaultCreativeBrief,
  DEFAULT_TEMPLATE_FAMILY_ID,
  getTemplateFamily,
  getTemplateFamilyOptions,
} from "../data/templateFamilyRegistry";
import {
  getCreativeBriefPalette,
  mapRegistryAssetToCreatePayload,
} from "../data/templateAdminHelpers";
import { buildTemplateDraft } from "../data/templateDraftBuilders";
import { compositionPlanToPolotnoDraft } from "../data/compositionPlanToPolotnoDraft";
import { evaluateCompositionQuality } from "../data/compositionQuality";
import { applySceneRecipes } from "../data/sceneRecipes";
import {
  getAdminAiTemplateDefaults,
  summarizeV2Brief,
} from "../data/adminAiTemplateDefaults";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const AI_TEMPLATE_WORKSPACE_STORAGE_KEY = "admin-ai-template-create-workspace-v1";

function loadStoredAiTemplateWorkspace() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const stored = window.sessionStorage.getItem(AI_TEMPLATE_WORKSPACE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Unable to restore admin AI template workspace:", error);
    return null;
  }
}

function textToTags(value = "") {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function tagsToText(value = []) {
  return Array.isArray(value) ? value.join(", ") : String(value || "");
}

function getAssetPreviewUrl(asset) {
  return asset?.thumbnail_url || asset?.file_url || "";
}

function isFetchableAssetUrl(url = "") {
  const normalizedUrl = String(url || "").trim().toLowerCase();
  return (
    (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://")) &&
    !normalizedUrl.startsWith("pending://") &&
    !normalizedUrl.includes("/pending/")
  );
}

function getAssetMediaUrl(asset) {
  if (asset?.id) {
    return `${API_URL}/templates/asset-library/${asset.id}/media`;
  }

  const directUrl = asset?.file_url || asset?.thumbnail_url || "";
  return isFetchableAssetUrl(directUrl) ? directUrl : getAssetPreviewUrl(asset);
}

function stripV2Fields(form) {
  const {
    v2_brief,
    defaults_sources,
    defaults_warnings,
    template_asset_bindings,
    ...legacyForm
  } = form;
  return legacyForm;
}

function getTemplateAssetBindings(form = {}) {
  return Array.isArray(form.template_asset_bindings) ? form.template_asset_bindings : [];
}

function buildFamilyDefaultsForm(familyId, currentForm = {}) {
  const family = getTemplateFamily(familyId);
  const baseForm = buildDefaultCreativeBrief(familyId);
  const subeventType = family.defaultSubeventTypes?.[0] || "";
  const defaultsResult = getAdminAiTemplateDefaults({
    templateName: currentForm.template_name || baseForm.template_name,
    eventType: family.eventType,
    subeventType,
    styleFamily: family.styleFamily,
    format: family.format,
  });
  const brief = defaultsResult.brief;
  const nextSceneCount = family.supportedSceneCounts.includes(brief.scene_count)
    ? brief.scene_count
    : baseForm.scene_count;

  return {
    form: {
      ...baseForm,
      template_name: currentForm.template_name || baseForm.template_name,
      event_type: brief.event_type || baseForm.event_type,
      style_family: brief.style_family || baseForm.style_family,
      format: brief.format || baseForm.format,
      scene_count: nextSceneCount,
      palette_text: brief.palette?.notes || baseForm.palette_text,
      palette_primary: brief.palette?.primary || baseForm.palette_primary,
      palette_secondary: brief.palette?.secondary || baseForm.palette_secondary,
      palette_accent: brief.palette?.accent || baseForm.palette_accent,
      palette_background: brief.palette?.background || baseForm.palette_background,
      palette_text_color: brief.palette?.text || baseForm.palette_text_color,
      style_preset_id: brief.style_direction?.style_preset_id || baseForm.style_preset_id,
      style_prompt: brief.style_direction?.style_prompt || baseForm.style_prompt,
      mood_text: (brief.style_direction?.mood || []).join(", ") || baseForm.mood_text,
      typography_direction:
        brief.style_direction?.typography_direction || baseForm.typography_direction,
      ornament_density: brief.style_direction?.ornament_density || baseForm.ornament_density,
      copy_style: brief.style_direction?.copy_tone || baseForm.copy_style,
      cultural_context_text: brief.subevent_type
        ? prettifyToken(brief.subevent_type)
        : baseForm.cultural_context_text,
      v2_brief: brief,
      defaults_sources: defaultsResult.sources,
      defaults_warnings: defaultsResult.warnings,
    },
    defaultsResult,
  };
}

function hasUsableMedia(asset) {
  const previewUrl = getAssetPreviewUrl(asset);
  const rawUrl = String(previewUrl || asset?.file_url || "");

  return (
    Boolean(previewUrl) &&
    asset?.asset_metadata?.has_media !== false &&
    !rawUrl.startsWith("pending://") &&
    !rawUrl.includes("/pending/")
  );
}

function isApprovedUsableAsset(asset) {
  return asset?.status === "available" && hasUsableMedia(asset);
}

function getAssetSearchText(asset) {
  return [
    asset?.name,
    asset?.asset_type,
    asset?.asset_group,
    asset?.asset_role,
    asset?.source_type,
    asset?.storage_path,
    ...(asset?.event_types || []),
    ...(asset?.style_tags || []),
    ...(asset?.culture_tags || []),
    ...(asset?.usage_tags || []),
    ...(asset?.color_tags || []),
    ...(asset?.language_tags || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function openTemplateEditor({ draftId, draftJson, templateMetadata, pendingTemplateAssets = [] }) {
  const templateId = draftId;
  if (draftJson) {
    window.sessionStorage.setItem(`template-editor-draft:${templateId}`, JSON.stringify(draftJson));
  }
  if (templateMetadata) {
    window.sessionStorage.setItem(
      `template-editor-template:${templateId}`,
      JSON.stringify(templateMetadata)
    );
  }
  if (pendingTemplateAssets.length) {
    window.sessionStorage.setItem(
      `template-editor-pending-assets:${templateId}`,
      JSON.stringify(pendingTemplateAssets)
    );
  }
  window.sessionStorage.setItem(`template-editor-mode:${templateId}`, "template");
  window.location.assign(`/editor/${templateId}`);
}

function normalizeAssetText(value = "") {
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function roleMatchesAsset(roleConfig, asset) {
  const role = normalizeAssetText(roleConfig.role);
  const exactRole = normalizeAssetText(asset?.asset_role);
  const usageTags = (asset?.usage_tags || []).map(normalizeAssetText);
  const searchText = normalizeAssetText(getAssetSearchText(asset));

  return exactRole === role || usageTags.includes(role) || searchText.includes(role);
}

const ASSET_ROLE_ALIASES = {
  ganesha_hero_background: ["static_stage_background", "ganesha_hero_background"],
  static_stage_background: ["static_stage_background", "ganesha_hero_background"],
  ganesha_icon_or_silhouette: ["ganesha_center_artwork", "ganesha_icon_or_silhouette"],
  ganesha_center_artwork: ["ganesha_center_artwork", "ganesha_icon_or_silhouette"],
  marigold_frame: ["marigold_top_garland", "marigold_frame", "marigold_side_accents"],
  marigold_top_garland: ["marigold_top_garland", "marigold_frame"],
  closing_background: ["closing_background", "static_stage_background", "ganesha_hero_background"],
};

function getRoleAliases(role) {
  const normalizedRole = normalizeAssetText(role).replace(/\s+/g, "_");
  const aliases = ASSET_ROLE_ALIASES[normalizedRole] || [];
  return new Set([normalizedRole, ...aliases.map((alias) => normalizeAssetText(alias).replace(/\s+/g, "_"))]);
}

function getFamilyDraftSceneIds(family) {
  if (family?.draftBuilder === "ganesh_chaturthi") {
    return [
      "scene-ganesh-opening",
      "scene-ganesh-host",
      "scene-ganesh-details",
      "scene-ganesh-rsvp",
      "scene-ganesh-closing",
    ];
  }
  if (family?.draftBuilder === "birthday_party") {
    return ["scene-birthday-opening", "scene-birthday-details", "scene-birthday-rsvp"];
  }

  return [
    "scene-grand-opening",
    "scene-invitation-message",
    "scene-ceremony-details",
    "scene-venue-address",
    "scene-closing-rsvp",
  ];
}

function inferV2Placement(role, sceneId) {
  const roleText = normalizeAssetText(role);
  const base = { sceneId, mode: "contain", opacity: 0.9 };

  if (roleText.includes("background")) {
    return { ...base, slot: "background.v2", mode: "cover", opacity: 1 };
  }
  if (roleText.includes("overlay") || roleText.includes("glow") || roleText.includes("particle")) {
    return { ...base, slot: "overlay.v2", mode: "cover", opacity: 0.45 };
  }
  if (roleText.includes("garland") || roleText.includes("marigold top")) {
    return { ...base, slot: "frame.top", x: 90, y: 82, width: 900, height: 190, opacity: 0.88 };
  }
  if (roleText.includes("ganesha") || roleText.includes("center artwork") || roleText.includes("center art")) {
    return { ...base, slot: "ornament.centerpiece", x: 315, y: 430, width: 450, height: 450, opacity: 0.94 };
  }
  if (roleText.includes("flower") && roleText.includes("left")) {
    return { ...base, slot: "ornament.offering.left", x: 95, y: 1300, width: 420, height: 260, opacity: 0.92 };
  }
  if (roleText.includes("flower") && roleText.includes("right")) {
    return { ...base, slot: "ornament.offering.right", x: 565, y: 1300, width: 420, height: 260, opacity: 0.92 };
  }
  if (roleText.includes("flower")) {
    return { ...base, slot: "ornament.offering", x: 270, y: 1320, width: 540, height: 260, opacity: 0.9 };
  }
  if (roleText.includes("rangoli") || roleText.includes("floor")) {
    return { ...base, slot: "ornament.floor", x: 240, y: 1460, width: 600, height: 260, opacity: 0.76 };
  }
  if (roleText.includes("divider")) {
    return { ...base, slot: "divider.v2", x: 270, y: 650, width: 540, height: 80, opacity: 0.9 };
  }
  if (roleText.includes("panel") || roleText.includes("card")) {
    return { ...base, slot: "panel.v2", x: 120, y: 430, width: 840, height: 980, opacity: 0.88 };
  }

  return { ...base, slot: "ornament.v2", x: 300, y: 1180, width: 480, height: 320, opacity: 0.84 };
}

function buildV2AssetRoleConfigs(v2Brief, family) {
  const assetPlanAssets = Array.isArray(v2Brief?.asset_plan?.assets)
    ? v2Brief.asset_plan.assets
    : [];
  if (!assetPlanAssets.length) return [];

  const draftSceneIds = getFamilyDraftSceneIds(family);
  const sceneBriefs = Array.isArray(v2Brief?.scene_briefs) ? v2Brief.scene_briefs : [];
  const sceneIdByBriefId = new Map(
    sceneBriefs.map((scene, index) => [scene.scene_id, draftSceneIds[index] || scene.scene_id])
  );

  return assetPlanAssets
    .map((asset) => {
      const role = asset.role || asset.asset_key;
      if (!role) return null;

      const referencedScenes = sceneBriefs
        .filter((scene) => (scene.asset_roles || []).includes(role))
        .map((scene) => scene.scene_id);
      const targetScenes = asset.target_scenes?.length ? asset.target_scenes : referencedScenes;
      const draftTargetScenes = targetScenes
        .map((sceneId) => sceneIdByBriefId.get(sceneId) || sceneId)
        .filter((sceneId) => draftSceneIds.includes(sceneId));
      const placementSceneIds = draftTargetScenes.length ? draftTargetScenes : draftSceneIds.slice(0, 1);

      return {
        role,
        name: prettifyToken(role),
        group: asset.asset_group || "v2_planned",
        assetType: asset.transparent_background_required ? "image" : "image",
        required: asset.source_strategy === "generate_new_asset",
        placementSummary: `${prettifyToken(role)} on ${placementSceneIds.length} scene(s)`,
        placement: placementSceneIds.map((sceneId) => inferV2Placement(role, sceneId)),
        usageTags: [role, asset.asset_key, asset.source_strategy, ...(asset.target_scenes || [])].filter(Boolean),
        source: "v2_brief",
      };
    })
    .filter(Boolean);
}

function getRequirementRole(requirement) {
  const candidates = [
    requirement?.role,
    requirement?.asset_role,
    requirement?.asset_key,
    requirement?.id,
    requirement?.name,
    requirement?.description,
    requirement?.generation_prompt,
  ];
  const canonicalRole = candidates.map(canonicalizeAssetRole).find(Boolean);
  return canonicalRole || requirement?.role || requirement?.asset_role || requirement?.id || "decorative_asset";
}

function getRequirementGroup(requirement) {
  return requirement?.asset_group || requirement?.group || "";
}

function getRequirementType(requirement) {
  const rawType = String(requirement?.asset_type || requirement?.type || "image").toLowerCase();
  if (rawType.includes("svg")) return "svg";
  if (rawType.includes("video")) return "video";
  if (rawType.includes("audio")) return "audio";
  if (rawType.includes("gif")) return "gif";
  return "image";
}

function prettifyToken(value = "") {
  return String(value)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function canonicalizeAssetRole(value = "") {
  const normalized = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (normalized.includes("flower offering left")) return "flower_offering_left";
  if (normalized.includes("flower offering right")) return "flower_offering_right";
  if (normalized.includes("marigold top garland")) return "marigold_top_garland";
  if (normalized.includes("diya glow overlay")) return "diya_glow_overlay";
  if (normalized.includes("rangoli floor accent")) return "rangoli_floor_accent";
  if (normalized.includes("ganesha center artwork")) return "ganesha_center_artwork";
  if (normalized.includes("static stage background")) return "static_stage_background";

  return "";
}

function isFullScreenPlacement(placement) {
  const slot = String(placement?.slot || "").toLowerCase();
  const mode = String(placement?.mode || "").toLowerCase();
  return mode === "cover" || slot.includes("background") || slot.includes("full");
}

function getRequirementPlacementProfile(requirement, family) {
  const role = getRequirementRole(requirement);
  const roleConfig = (family.generatableAssetRoles || []).find((item) => item.role === role);
  const placements = requirement?.placement || roleConfig?.placement || [];
  const roleText = [
    role,
    getRequirementGroup(requirement),
    requirement?.description,
    requirement?.name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const isFullScreen =
    placements.some(isFullScreenPlacement) ||
    roleText.includes("background") ||
    roleText.includes("full screen") ||
    roleText.includes("full_scene");

  if (isFullScreen) {
    return {
      intended_use: "mobile_full_screen",
      intended_aspect_ratio: "9:16",
      intended_width: 1080,
      intended_height: 1920,
      generation_size_hint: "1024x1536",
      placement: placements,
    };
  }

  return {
    intended_use: "reusable_overlay",
    intended_aspect_ratio: "asset_native",
    intended_width: null,
    intended_height: null,
    generation_size_hint: "1024x1024",
    placement: placements,
  };
}

function getRoleSpecificPromptGuard(role, placementProfile) {
  if (
    (role === "ganesha_hero_background" || role === "static_stage_background") &&
    placementProfile.intended_use === "mobile_full_screen"
  ) {
    return (
      "CRITICAL layering requirement: this asset is only the static background stage. " +
      "Ignore any earlier instruction asking for a Ganesha silhouette, icon, idol, or figure. " +
      "Do not include Ganesha, Ganpati, " +
      "deity figures, idols, statues, silhouettes, faces, hands, trunk, mouse, or any main character. " +
      "Do not include diya lamps, flames, marigold garlands, flowers, rangoli designs, particles, " +
      "sparkles, bells, kalash, invitation text, or festival title text. Those elements are separate " +
      "movable animation layers. Use only saffron-gold texture, soft ambient light, depth, and empty safe space."
    );
  }

  return "";
}

function getRoleSpecificBasePrompt(role) {
  if (role === "ganesha_hero_background" || role === "static_stage_background") {
    return (
      "Create ONE vertical Ganesh Chaturthi static stage background for a 1080x1920 mobile video canvas. " +
      "This asset must be background only: saffron, vermillion, antique gold, and warm ivory textures, " +
      "soft devotional ambient lighting, subtle vignette depth, and empty safe space for overlaid editable text and separate animated decorative layers. " +
      "Do not include any foreground objects, deity artwork, flowers, garlands, diya lamps, rangoli, particles, or text."
    );
  }

  if (role === "ganesha_center_artwork" || role === "ganesha_icon_or_silhouette") {
    return (
      "Create ONE isolated transparent-background Lord Ganesha center artwork cutout asset for a Ganesh Chaturthi Polotno video template. " +
      "This asset must contain only respectful premium Ganesha artwork, centered and cleanly cropped for reveal animation. " +
      "Transparent background required with real alpha channel. Do not add scenery, frame, garland, flowers, diya, rangoli, text, poster layout, or background."
    );
  }

  if (role === "flower_offering_left") {
    return (
      "Create ONE isolated transparent-background flower offering cutout asset. " +
      "This asset must contain only a compact cluster of marigold blossoms, rose petals, and a few lotus-like petals, " +
      "angled from lower-left toward center for side-entry animation. Tight crop around the flowers. " +
      "Transparent background required with real alpha channel. Do not add white, black, gray, solid, scene, or checkerboard background. No other objects."
    );
  }

  if (role === "flower_offering_right") {
    return (
      "Create ONE isolated transparent-background flower offering cutout asset. " +
      "This asset must contain only a compact cluster of marigold blossoms, rose petals, and a few lotus-like petals, " +
      "angled from lower-right toward center for side-entry animation. Tight crop around the flowers. " +
      "Transparent background required with real alpha channel. Do not add white, black, gray, solid, scene, or checkerboard background. No other objects."
    );
  }

  if (role === "marigold_top_garland") {
    return (
      "Create ONE isolated transparent-background horizontal marigold top garland cutout asset. " +
      "This asset must contain only an elegant horizontal garland made of orange and yellow marigold flowers, " +
      "small green leaves, and subtle gold thread accents. It should be suitable for sliding down from the top edge of a vertical mobile video. " +
      "Transparent background required with real alpha channel. Do not add white, black, gray, solid, scene, or checkerboard background. No other objects."
    );
  }

  if (role === "diya_glow_overlay") {
    return (
      "Create ONE isolated transparent-background warm diya glow overlay asset. " +
      "This asset should contain only soft golden-orange light blooms suitable for lower-corner devotional glow in a Polotno video template. " +
      "Transparent background required with real alpha channel. Do not include visible lamps, flames, Ganesha, garlands, flowers, rangoli, scene background, or text."
    );
  }

  if (role === "ganesha_halo_glow" || role === "hero_halo_glow") {
    return (
      "Create ONE isolated transparent-background soft golden halo glow overlay asset for placing behind Ganesha artwork. " +
      "This asset must contain only a warm circular/radial glow with subtle devotional light falloff. " +
      "Transparent background required with real alpha channel. Do not include Ganesha, flowers, garlands, diya, rangoli, background scenery, or text."
    );
  }

  return "";
}

function getRoleSpecificNegativePrompt(role) {
  if (role === "ganesha_hero_background" || role === "static_stage_background") {
    return "Ganesha, Ganpati, deity, god, idol, statue, face, silhouette, icon, trunk, hands, mouse, character, person, diya, lamp, flame, marigold, garland, flower, rangoli, particles, sparkles, bell, kalash, foreground object, shubh nimantran, invitation text, title text, typography, letters, words, watermark, logo";
  }

  if (role === "ganesha_center_artwork" || role === "ganesha_icon_or_silhouette") {
    return "background, scenery, poster, full scene, frame, border, garland, marigold, flower, flower offering, diya, lamp, flame, rangoli, title, text, typography, words, letters, watermark, logo, distorted face, extra limbs, low quality";
  }

  if (role === "flower_offering_left" || role === "flower_offering_right") {
    return "Ganesha, Ganpati, deity, idol, statue, silhouette, face, trunk, hands, mouse, garland, marigold garland, frame, border, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
  }

  if (role === "marigold_top_garland") {
    return "Ganesha, Ganpati, deity, idol, statue, silhouette, face, trunk, hands, mouse, flower offering, loose petals, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
  }

  if (role === "diya_glow_overlay") {
    return "Ganesha, Ganpati, deity, idol, statue, visible diya lamp, visible flame, wick, garland, marigold, flower, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
  }

  if (role === "ganesha_halo_glow" || role === "hero_halo_glow") {
    return "Ganesha, Ganpati, deity, idol, statue, face, trunk, hands, garland, marigold, flower, diya, lamp, flame, rangoli, floor, background, white background, black background, gray background, solid background, checkerboard, transparency grid, full scene, poster, invitation, title, text, typography, letters, words, watermark, logo";
  }

  return "";
}

function isIsolatedMovableAssetRole(role, placementProfile) {
  if (placementProfile?.intended_use === "mobile_full_screen") return false;
  return [
    "flower_offering_left",
    "flower_offering_right",
    "marigold_top_garland",
    "marigold_bottom_accent",
    "marigold_side_accents",
    "diya_glow_overlay",
    "rangoli_floor_accent",
    "lotus_divider",
    "small_diya_icon",
    "temple_bell_icon",
    "modak_icon",
  ].includes(role);
}

function buildAssetStyleContext({ form, placementProfile, isolatedAsset }) {
  if (isolatedAsset) {
    return [
      form.palette_text ? `Use this color direction only where relevant to this single asset: ${form.palette_text}.` : "",
      "Do not turn this into a complete invitation scene. Do not add extra cultural objects unless the asset prompt explicitly asks for them.",
    ]
      .filter(Boolean)
      .join(" ");
  }

  return [
    form.style_prompt ? `Style direction: ${form.style_prompt}` : "",
    form.palette_text ? `Palette: ${form.palette_text}` : "",
    form.mood_text ? `Mood: ${form.mood_text}` : "",
    placementProfile?.intended_use === "mobile_full_screen"
      ? "Keep movable/decorative objects separate unless the prompt explicitly asks for a full background."
      : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function buildAssetBriefPayload({ requirement, family, form, templateId }) {
  const role = getRequirementRole(requirement);
  const group = getRequirementGroup(requirement);
  const assetType = getRequirementType(requirement);
  const requirementId = requirement?.id || role;
  const stylePresetId = form.style_preset_id || "custom";
  const placementProfile = getRequirementPlacementProfile(requirement, family);
  const roleSpecificBasePrompt = getRoleSpecificBasePrompt(role);
  const isolatedAsset = isIsolatedMovableAssetRole(role, placementProfile);
  const basePrompt =
    roleSpecificBasePrompt ||
    requirement?.generation_prompt ||
    requirement?.description ||
    "";
  const prompt = [
      basePrompt,
      roleSpecificBasePrompt ? "" : requirement?.description,
      placementProfile.intended_use === "mobile_full_screen"
        ? "Format requirement: vertical mobile invitation background, 9:16 portrait composition, designed for a 1080x1920 canvas with safe readable text space."
        : "",
      getRoleSpecificPromptGuard(role, placementProfile),
      buildAssetStyleContext({ form, placementProfile, isolatedAsset }),
    ]
      .filter(Boolean)
      .join(" ");
  const negativePrompt = [
    requirement?.negative_prompt,
    getRoleSpecificNegativePrompt(role),
    "No text, no names, no watermark, no logo, no distorted ornament, no low-resolution artifacts.",
  ]
    .filter(Boolean)
    .join(", ");

  return {
    name: `${family.label} ${prettifyToken(role)}`,
    asset_type: assetType,
    asset_group: group || role.split("_")[0] || null,
    asset_role: role,
    file_url: `/template-assets/generated/pending/${family.id}/${stylePresetId}/${role}`,
    thumbnail_url: null,
    storage_path: `template-assets/generated/${family.id}/${stylePresetId}/${role}`,
    source_type: "ai_generated",
    status: "needs_review",
    event_types: [family.eventType],
    style_tags: [
      family.styleFamily,
      family.eventType,
      "ai-brief",
      "template-requirement",
      form.style_preset_id,
      ...textToTags(form.mood_text || ""),
    ].filter(Boolean),
    culture_tags: textToTags(form.cultural_context_text || family.defaultCultureText),
    religion_tags: [],
    language_tags: [],
    usage_tags: [
      role,
      group,
      "template_requirement",
      ...(Array.isArray(requirement?.usage_tags) ? requirement.usage_tags : []),
    ].filter(Boolean),
    color_tags: textToTags(form.palette_text || "").map((tag) => tag.replace(/^#/, "").toLowerCase()),
    generation_prompt: prompt,
    negative_prompt: negativePrompt,
    license_notes:
      "AI asset brief created from admin template requirement. Review before publishing.",
    asset_metadata: {
      generation_status: "brief_only",
      source_workflow: "admin_ai_template_requirement",
      template_family_id: family.id,
      source_template_id: templateId?.startsWith("admin-draft-") ? null : templateId,
      requirement_id: requirementId,
      target_scenes: requirement?.target_scenes || [],
      ...placementProfile,
      original_requirement: requirement,
    },
  };
}

export function AdminAiTemplateCreatePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [restoredWorkspace] = useState(() => loadStoredAiTemplateWorkspace());
  const [selectedFamilyId, setSelectedFamilyId] = useState(
    () => restoredWorkspace?.selectedFamilyId || DEFAULT_TEMPLATE_FAMILY_ID
  );
  const activeFamily = useMemo(() => getTemplateFamily(selectedFamilyId), [selectedFamilyId]);
  const familyOptions = useMemo(() => getTemplateFamilyOptions(), []);
  const [form, setForm] = useState(
    () =>
      restoredWorkspace?.form ||
      buildFamilyDefaultsForm(restoredWorkspace?.selectedFamilyId || DEFAULT_TEMPLATE_FAMILY_ID).form
  );
  const [assetLibraryItems, setAssetLibraryItems] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingComposition, setIsGeneratingComposition] = useState(false);
  const [isRefiningComposition, setIsRefiningComposition] = useState(false);
  const [compositionFeedback, setCompositionFeedback] = useState(restoredWorkspace?.compositionFeedback || "");
  const [createdDraft, setCreatedDraft] = useState(restoredWorkspace?.createdDraft || null);
  const [compositionResult, setCompositionResult] = useState(restoredWorkspace?.compositionResult || null);
  const [assetRequirements, setAssetRequirements] = useState(restoredWorkspace?.assetRequirements || []);
  const [createdRequirementKeys, setCreatedRequirementKeys] = useState(() => new Set());
  const [creatingRequirementKey, setCreatingRequirementKey] = useState("");
  const [isCreatingV2Briefs, setIsCreatingV2Briefs] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("idle");

  const aiAssetPlan = useMemo(
    () => getTemplateAssetPlan(activeFamily.assetFamilyId),
    [activeFamily.assetFamilyId]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadAssets() {
      try {
        const items = await getAssetLibraryItems({ limit: 300 });
        if (isMounted) {
          setAssetLibraryItems(items || []);
        }
      } catch (error) {
        console.error("Unable to load asset library:", error);
        if (isMounted) {
          setStatusTone("error");
          setStatusMessage(error?.message || "Unable to load asset library.");
        }
      }
    }

    loadAssets();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const workspace = {
      selectedFamilyId,
      form,
      compositionFeedback,
      compositionResult,
      createdDraft,
      assetRequirements,
      savedAt: new Date().toISOString(),
    };
    window.sessionStorage.setItem(
      AI_TEMPLATE_WORKSPACE_STORAGE_KEY,
      JSON.stringify(workspace)
    );
  }, [
    assetRequirements,
    compositionFeedback,
    compositionResult,
    createdDraft,
    form,
    selectedFamilyId,
  ]);

  const approvedUsableAssets = useMemo(
    () => assetLibraryItems.filter(isApprovedUsableAsset),
    [assetLibraryItems]
  );

  const selectableAssets = approvedUsableAssets;

  const selectedAssetsByRole = useMemo(() => {
    const roleMap = new Map();
    const addAssetRole = (asset) => {
      if (asset.binding_scope === "scene_slot") return;
      if (!asset.role) return;
      getRoleAliases(asset.role).forEach((alias) => roleMap.set(alias, asset));
    };
    (form.selected_assets || []).forEach((asset) => {
      addAssetRole(asset);
    });
    getTemplateAssetBindings(form).forEach((asset) => {
      addAssetRole(asset);
    });
    return roleMap;
  }, [form]);

  const selectedAssetsBySceneSlot = useMemo(() => {
    const slotMap = new Map();
    getTemplateAssetBindings(form)
      .filter((asset) => asset.binding_scope === "scene_slot")
      .forEach((asset) => {
        if (asset.slot_key) {
          slotMap.set(asset.slot_key, asset);
        }
        if (asset.scene_id && asset.element_id) {
          slotMap.set(`${asset.scene_id}:${asset.element_id}`, asset);
        }
      });
    return slotMap;
  }, [form]);

  const effectiveSelectedAssets = useMemo(() => {
    const roleMap = new Map();
    const sceneSlotAssets = [];
    const addAsset = (asset) => {
      if (asset.binding_scope === "scene_slot") {
        sceneSlotAssets.push(asset);
        return;
      }
      if (asset.role) {
        roleMap.set(normalizeAssetText(asset.role), asset);
      }
    };
    (form.selected_assets || []).forEach((asset) => {
      addAsset(asset);
    });
    getTemplateAssetBindings(form).forEach((asset) => {
      addAsset(asset);
    });
    const seenSceneSlots = new Set();
    const uniqueSceneSlotAssets = sceneSlotAssets.filter((asset) => {
      const key = asset.slot_key || `${asset.scene_id || ""}:${asset.element_id || ""}:${asset.asset_library_item_id || ""}`;
      if (seenSceneSlots.has(key)) return false;
      seenSceneSlots.add(key);
      return true;
    });
    return [...Array.from(roleMap.values()), ...uniqueSceneSlotAssets];
  }, [form]);

  const assetLibraryById = useMemo(() => {
    const assetMap = new Map();
    assetLibraryItems.forEach((asset) => {
      assetMap.set(String(asset.id), asset);
    });
    return assetMap;
  }, [assetLibraryItems]);

  const assetRoleSuggestions = useMemo(() => {
    const suggestions = new Map();

    activeFamily.generatableAssetRoles.forEach((roleConfig) => {
      const directMatch = approvedUsableAssets.find(
        (asset) => normalizeAssetText(asset.asset_role) === normalizeAssetText(roleConfig.role)
      );
      const broadMatch =
        directMatch ||
        approvedUsableAssets.find((asset) => roleMatchesAsset(roleConfig, asset));

      if (broadMatch) {
        suggestions.set(roleConfig.role, broadMatch);
      }
    });

    return suggestions;
  }, [activeFamily.generatableAssetRoles, approvedUsableAssets]);

  const v2AssetRoleConfigs = useMemo(
    () => buildV2AssetRoleConfigs(form.v2_brief, activeFamily),
    [activeFamily, form.v2_brief]
  );

  const v2AssetRoleSuggestions = useMemo(() => {
    const suggestions = new Map();

    v2AssetRoleConfigs.forEach((roleConfig) => {
      const directMatch = selectableAssets.find(
        (asset) => normalizeAssetText(asset.asset_role) === normalizeAssetText(roleConfig.role)
      );
      const broadMatch =
        directMatch ||
        selectableAssets.find((asset) => roleMatchesAsset(roleConfig, asset));

      if (broadMatch) {
        suggestions.set(roleConfig.role, broadMatch);
      }
    });

    return suggestions;
  }, [selectableAssets, v2AssetRoleConfigs]);

  const matchingLibraryRoles = useMemo(() => {
    return new Set(
      activeFamily.generatableAssetRoles.filter((roleConfig) =>
        assetRoleSuggestions.has(roleConfig.role)
      ).map((roleConfig) => roleConfig.role)
    );
  }, [activeFamily.generatableAssetRoles, assetRoleSuggestions]);

  const missingLibraryRoles = useMemo(
    () => activeFamily.generatableAssetRoles.filter(
      (roleConfig) => !matchingLibraryRoles.has(roleConfig.role)
    ),
    [activeFamily.generatableAssetRoles, matchingLibraryRoles]
  );
  const v2MissingLibraryRoles = useMemo(
    () => v2AssetRoleConfigs.filter((roleConfig) => !v2AssetRoleSuggestions.has(roleConfig.role)),
    [v2AssetRoleConfigs, v2AssetRoleSuggestions]
  );
  const v2BriefSummary = useMemo(
    () => summarizeV2Brief(form.v2_brief),
    [form.v2_brief]
  );
  const compositionPlan = compositionResult?.composition_plan;
  const compositionScenes = Array.isArray(compositionPlan?.scenes) ? compositionPlan.scenes : [];
  const compositionWarnings = compositionPlan?.composition_summary?.warnings || [];
  const compositionAssetSummary = compositionPlan?.asset_resolution_summary || {};
  const buildMissingRoleRequirement = useCallback(
    (roleConfigOrRole, source = "missing_role") => {
      const role =
        typeof roleConfigOrRole === "string"
          ? roleConfigOrRole
          : roleConfigOrRole?.role || "decorative_asset";
      const roleConfig =
        typeof roleConfigOrRole === "string"
          ? (activeFamily.generatableAssetRoles || []).find(
              (item) => normalizeAssetText(item.role) === normalizeAssetText(role)
            ) ||
            v2AssetRoleConfigs.find(
              (item) => normalizeAssetText(item.role) === normalizeAssetText(role)
            )
          : roleConfigOrRole;

      return {
        id: role,
        role,
        asset_role: role,
        asset_group: roleConfig?.group || "composition_missing",
        type: roleConfig?.assetType || "image",
        description:
          roleConfig?.placementSummary ||
          `Create ${prettifyToken(role)} for ${activeFamily.label}.`,
        generation_prompt:
          roleConfig?.placementSummary ||
          `Create a reusable ${prettifyToken(role)} asset for ${activeFamily.label}. It should match the current palette and work as a separate Polotno layer.`,
        placement: roleConfig?.placement || [],
        usage_tags: [role, "composition_missing", source, "admin_ai_template"].filter(Boolean),
        source,
      };
    },
    [activeFamily, v2AssetRoleConfigs]
  );
  const compositionMissingRoles = useMemo(
    () => {
      const boundRoles = new Set();
      effectiveSelectedAssets.forEach((asset) => {
        getRoleAliases(asset.role || asset.asset_role || asset.asset_key).forEach((alias) => boundRoles.add(alias));
      });

      return Array.from(
        new Set([
          ...(compositionAssetSummary.missing_roles || []),
          ...(compositionAssetSummary.newly_proposed_roles || []),
        ].filter(Boolean))
      ).filter((role) => !Array.from(getRoleAliases(role)).some((alias) => boundRoles.has(alias)));
    },
    [compositionAssetSummary.missing_roles, compositionAssetSummary.newly_proposed_roles, effectiveSelectedAssets]
  );
  const compositionMissingRoleRequirements = (() => {
    const planRequirements = Array.isArray(compositionPlan?.asset_requirements)
      ? compositionPlan.asset_requirements
      : [];

    return compositionMissingRoles.map((role) => {
      const normalizedRole = normalizeAssetText(role);
      const planRequirement = planRequirements.find(
        (requirement) => normalizeAssetText(getRequirementRole(requirement)) === normalizedRole
      );
      if (planRequirement) {
        return planRequirement;
      }

      const v2RoleConfig = v2AssetRoleConfigs.find(
        (roleConfig) => normalizeAssetText(roleConfig.role) === normalizedRole
      );
      if (v2RoleConfig) {
        return buildMissingRoleRequirement(v2RoleConfig, "composition_missing_role");
      }

      const familyRoleConfig = (activeFamily.generatableAssetRoles || []).find(
        (roleConfig) => normalizeAssetText(roleConfig.role) === normalizedRole
      );
      if (familyRoleConfig) {
        return buildMissingRoleRequirement(familyRoleConfig, "composition_missing_role");
      }

      return buildMissingRoleRequirement(role, "composition_missing_role");
    });
  })();
  const enrichedCompositionPlan = useMemo(
    () =>
      compositionPlan
        ? applySceneRecipes(compositionPlan, effectiveSelectedAssets, activeFamily)
        : null,
    [activeFamily, compositionPlan, effectiveSelectedAssets]
  );
  const compositionQuality = useMemo(
    () =>
      compositionPlan
        ? evaluateCompositionQuality({
            compositionPlan,
            selectedAssets: effectiveSelectedAssets,
            family: activeFamily,
          })
        : null,
    [activeFamily, compositionPlan, effectiveSelectedAssets]
  );
  const enrichedCompositionQuality = useMemo(
    () =>
      enrichedCompositionPlan
        ? evaluateCompositionQuality({
            compositionPlan: enrichedCompositionPlan,
            selectedAssets: effectiveSelectedAssets,
            family: activeFamily,
          })
        : null,
    [activeFamily, enrichedCompositionPlan, effectiveSelectedAssets]
  );
  const assetReadiness = enrichedCompositionQuality?.assetReadiness || compositionQuality?.assetReadiness || null;
  const unresolvedAssetRequirements = useMemo(() => {
    if (assetReadiness?.score === 100 && !compositionMissingRoles.length) {
      return [];
    }

    const selectedAliases = new Set();
    effectiveSelectedAssets.forEach((asset) => {
      getRoleAliases(asset.role || asset.asset_role || asset.asset_key).forEach((alias) =>
        selectedAliases.add(alias)
      );
    });

    return assetRequirements.filter((requirement) => {
      const role = getRequirementRole(requirement);
      const aliases = getRoleAliases(role);
      return !Array.from(aliases).some((alias) => selectedAliases.has(alias));
    });
  }, [assetReadiness?.score, assetRequirements, compositionMissingRoles.length, effectiveSelectedAssets]);
  const sceneAssetMappings = useMemo(() => {
    const planForSlots = enrichedCompositionPlan || compositionPlan;
    const scenes = Array.isArray(planForSlots?.scenes) ? planForSlots.scenes : [];

    return scenes.map((scene, sceneIndex) => {
      const slotMap = new Map();
      (scene.elements || []).forEach((element) => {
        const binding = element.asset_binding || {};
        const type = String(element.type || "").toLowerCase();
        const role = binding.asset_role || binding.asset_key || element.role || "";
        const normalizedRole = normalizeAssetText(role);
        const isAssetElement = ["image", "svg", "video"].includes(type) || Boolean(binding.asset_role || binding.asset_key);

        if (!isAssetElement || !normalizedRole || normalizedRole.startsWith("missing ")) {
          return;
        }

        const key = `${scene.scene_id || scene.page_id || sceneIndex}:${normalizedRole}:${element.id || slotMap.size}`;
        slotMap.set(key, {
          key,
          sceneId: scene.scene_id || scene.page_id || `scene-${sceneIndex + 1}`,
          sceneName: scene.name || scene.scene_id || `Scene ${sceneIndex + 1}`,
          elementId: element.id,
          role,
          normalizedRole,
          type,
          binding,
          placement: element.placement || {},
          animation: element.animation || [],
          required: !String(binding.placeholder_behavior || "").toLowerCase().includes("hide"),
        });
      });

      return {
        scene,
        sceneIndex,
        slots: Array.from(slotMap.values()),
      };
    });
  }, [compositionPlan, enrichedCompositionPlan]);

  const sceneAssetMappingStats = useMemo(() => {
    const slots = sceneAssetMappings.flatMap((scene) => scene.slots);
    const mapped = slots.filter((slot) => {
      const selected =
        selectedAssetsBySceneSlot.get(slot.key) ||
        selectedAssetsBySceneSlot.get(`${slot.sceneId}:${slot.elementId}`) ||
        selectedAssetsByRole.get(slot.role) ||
        selectedAssetsByRole.get(slot.normalizedRole);
      return Boolean(selected || slot.binding?.asset_library_item_id || slot.binding?.media_url);
    });

    return {
      total: slots.length,
      mapped: mapped.length,
      missing: Math.max(slots.length - mapped.length, 0),
    };
  }, [sceneAssetMappings, selectedAssetsByRole, selectedAssetsBySceneSlot]);
  const draftReadinessItems = useMemo(
    () => [
      {
        id: "composition",
        label: "V3 composition plan",
        ready: Boolean(compositionPlan),
        detail: compositionPlan
          ? `${compositionScenes.length} scenes planned`
          : "Generate a V3 composition plan before building the preferred draft.",
      },
      {
        id: "mapping",
        label: "Scene asset mapping",
        ready: Boolean(compositionPlan) && sceneAssetMappingStats.total > 0 && sceneAssetMappingStats.missing === 0,
        detail: compositionPlan
          ? `${sceneAssetMappingStats.mapped}/${sceneAssetMappingStats.total} scene asset slots mapped`
          : "Scene slots appear after V3 composition is generated.",
      },
      {
        id: "assets",
        label: "Required assets",
        ready: !assetReadiness || !assetReadiness.missingRequired.length,
        detail: assetReadiness
          ? `${assetReadiness.missingRequired.length} required missing, ${assetReadiness.missingRecommended.length} recommended missing`
          : "Asset readiness will update after defaults/assets are selected.",
      },
      {
        id: "source",
        label: "Draft source confirmed",
        ready: Boolean(compositionPlan),
        detail: compositionPlan
          ? "Generate Draft uses V3 composition + Scene Asset Mapping."
          : "Generate Draft will use template defaults until a V3 composition exists.",
      },
    ],
    [assetReadiness, compositionPlan, compositionScenes.length, sceneAssetMappingStats]
  );

  const getRoleCandidateAssets = useCallback(
    (role) => {
      const normalizedRole = normalizeAssetText(role);
      const roleConfig = { role };
      const exactMatches = selectableAssets.filter(
        (asset) => normalizeAssetText(asset.asset_role) === normalizedRole
      );
      const broadMatches = selectableAssets.filter(
        (asset) =>
          normalizeAssetText(asset.asset_role) !== normalizedRole &&
          roleMatchesAsset(roleConfig, asset)
      );
      return [...exactMatches, ...broadMatches];
    },
    [selectableAssets]
  );

  const resolveSceneSlotAsset = useCallback(
    (slot) => {
      const selected =
        selectedAssetsBySceneSlot.get(slot.key) ||
        selectedAssetsBySceneSlot.get(`${slot.sceneId}:${slot.elementId}`) ||
        selectedAssetsByRole.get(slot.role) ||
        selectedAssetsByRole.get(slot.normalizedRole);
      if (selected) {
        const libraryAsset = assetLibraryById.get(String(selected.asset_library_item_id));
        return {
          source: "selected_by_admin",
          selectedAsset: selected,
          libraryAsset,
          name: libraryAsset?.name || selected.asset_name || prettifyToken(slot.role),
          previewUrl: getAssetPreviewUrl(libraryAsset) || selected.preview_url || selected.media_url || "",
          status: libraryAsset?.status || "selected",
        };
      }

      const bindingAssetId = slot.binding?.asset_library_item_id;
      if (bindingAssetId) {
        const libraryAsset = assetLibraryById.get(String(bindingAssetId));
        return {
          source: "composition_binding",
          selectedAsset: null,
          libraryAsset,
          name: libraryAsset?.name || slot.binding?.asset_name || prettifyToken(slot.role),
          previewUrl: getAssetPreviewUrl(libraryAsset) || slot.binding?.media_url || "",
          status: libraryAsset?.status || "bound",
        };
      }

      if (slot.binding?.media_url) {
        return {
          source: "composition_media",
          selectedAsset: null,
          libraryAsset: null,
          name: slot.binding?.asset_name || prettifyToken(slot.role),
          previewUrl: slot.binding.media_url,
          status: "media",
        };
      }

      const suggestedAsset = getRoleCandidateAssets(slot.role)[0];
      if (suggestedAsset) {
        return {
          source: "suggested_by_role",
          selectedAsset: null,
          libraryAsset: suggestedAsset,
          name: suggestedAsset.name,
          previewUrl: getAssetPreviewUrl(suggestedAsset),
          status: suggestedAsset.status || "suggested",
        };
      }

      return {
        source: "missing",
        selectedAsset: null,
        libraryAsset: null,
        name: "No asset selected",
        previewUrl: "",
        status: "missing",
      };
    },
    [assetLibraryById, getRoleCandidateAssets, selectedAssetsByRole, selectedAssetsBySceneSlot]
  );

  const createAssetBriefForRole = (roleConfigOrRole, source = "missing_role") => {
    createAssetBrief(buildMissingRoleRequirement(roleConfigOrRole, source));
  };

  const focusAssetRole = (roleConfig) => {
    navigate("/admin/assets");
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const changeTemplateFamily = (familyId) => {
    const { form: defaultsForm, defaultsResult } = buildFamilyDefaultsForm(familyId, form);
    setSelectedFamilyId(familyId);
    setForm(defaultsForm);
    setCompositionFeedback("");
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusTone(defaultsResult.warnings.length ? "idle" : "success");
    setStatusMessage(
      defaultsResult.warnings.length
        ? `Family defaults loaded with fallback: ${defaultsResult.warnings[0]}`
        : "Family defaults loaded. Review the brief, then generate a composition plan."
    );
  };

  const clearSavedWorkspace = () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(AI_TEMPLATE_WORKSPACE_STORAGE_KEY);
    }
    const { form: defaultsForm } = buildFamilyDefaultsForm(DEFAULT_TEMPLATE_FAMILY_ID);
    setSelectedFamilyId(DEFAULT_TEMPLATE_FAMILY_ID);
    setCompositionFeedback("");
    setForm(defaultsForm);
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusTone("idle");
    setStatusMessage("Saved workspace cleared.");
  };

  const resetToFamilyDefaults = () => {
    const { form: defaultsForm, defaultsResult } = buildFamilyDefaultsForm(selectedFamilyId, form);

    setForm(defaultsForm);
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusTone(defaultsResult.warnings.length ? "idle" : "success");
    setStatusMessage(
      defaultsResult.warnings.length
        ? `Family defaults reset with fallback: ${defaultsResult.warnings[0]}`
        : "Family defaults reset. Review the brief, then generate a composition plan."
    );
  };

  const updateV2Scene = (sceneIndex, updateScene) => {
    setForm((current) => {
      if (!current.v2_brief) return current;

      const sceneBriefs = current.v2_brief.scene_briefs || [];
      return {
        ...current,
        v2_brief: {
          ...current.v2_brief,
          scene_briefs: sceneBriefs.map((scene, index) =>
            index === sceneIndex ? updateScene(scene) : scene
          ),
        },
      };
    });
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const updateV2SceneField = (sceneIndex, field, value) => {
    updateV2Scene(sceneIndex, (scene) => ({ ...scene, [field]: value }));
  };

  const updateV2SceneListField = (sceneIndex, field, value) => {
    updateV2Scene(sceneIndex, (scene) => ({ ...scene, [field]: textToTags(value) }));
  };

  const updateV2CopySlot = (sceneIndex, key, value) => {
    updateV2Scene(sceneIndex, (scene) => ({
      ...scene,
      copy_slots: {
        ...(scene.copy_slots || {}),
        [key]: value,
      },
    }));
  };

  const addV2CopySlot = (sceneIndex) => {
    updateV2Scene(sceneIndex, (scene) => {
      const copySlots = scene.copy_slots || {};
      let nextIndex = Object.keys(copySlots).length + 1;
      let nextKey = `custom_copy_${nextIndex}`;
      while (copySlots[nextKey]) {
        nextIndex += 1;
        nextKey = `custom_copy_${nextIndex}`;
      }

      return {
        ...scene,
        copy_slots: {
          ...copySlots,
          [nextKey]: "Editable copy",
        },
      };
    });
  };

  const removeV2CopySlot = (sceneIndex, keyToRemove) => {
    updateV2Scene(sceneIndex, (scene) => {
      const nextCopySlots = { ...(scene.copy_slots || {}) };
      delete nextCopySlots[keyToRemove];
      return { ...scene, copy_slots: nextCopySlots };
    });
  };

  const updateV2Asset = (assetIndex, updateAsset) => {
    setForm((current) => {
      if (!current.v2_brief) return current;

      const currentAssetPlan = current.v2_brief.asset_plan || { mode: "mixed", assets: [] };
      const assets = currentAssetPlan.assets || [];

      return {
        ...current,
        v2_brief: {
          ...current.v2_brief,
          asset_plan: {
            ...currentAssetPlan,
            assets: assets.map((asset, index) =>
              index === assetIndex ? updateAsset(asset) : asset
            ),
          },
        },
      };
    });
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const updateV2AssetField = (assetIndex, field, value) => {
    updateV2Asset(assetIndex, (asset) => ({ ...asset, [field]: value }));
  };

  const updateV2AssetListField = (assetIndex, field, value) => {
    updateV2Asset(assetIndex, (asset) => ({ ...asset, [field]: textToTags(value) }));
  };

  const updateV2AssetPlanField = (field, value) => {
    setForm((current) => {
      if (!current.v2_brief) return current;

      const currentAssetPlan = current.v2_brief.asset_plan || { mode: "mixed", assets: [] };

      return {
        ...current,
        v2_brief: {
          ...current.v2_brief,
          asset_plan: {
            ...currentAssetPlan,
            [field]: value,
          },
        },
      };
    });
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const addV2Asset = () => {
    setForm((current) => {
      if (!current.v2_brief) return current;

      const currentAssetPlan = current.v2_brief.asset_plan || { mode: "mixed", assets: [] };
      const assets = currentAssetPlan.assets || [];
      const nextIndex = assets.length + 1;

      return {
        ...current,
        v2_brief: {
          ...current.v2_brief,
          asset_plan: {
            ...currentAssetPlan,
            assets: [
              ...assets,
              {
                asset_key: `custom_asset_${nextIndex}`,
                role: `custom_asset_${nextIndex}`,
                purpose: "Describe what this asset should do in the template.",
                source_strategy: "generate_new_asset",
                target_scenes: [],
                prompt: "",
                negative_prompt: "text, watermark, low quality",
                transparent_background_required: true,
                aspect_ratio: "asset_native",
                intended_width: null,
                intended_height: null,
                usage_mode: "place_directly",
              },
            ],
          },
        },
      };
    });
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const removeV2Asset = (assetIndex) => {
    setForm((current) => {
      if (!current.v2_brief) return current;

      const currentAssetPlan = current.v2_brief.asset_plan || { mode: "mixed", assets: [] };
      return {
        ...current,
        v2_brief: {
          ...current.v2_brief,
          asset_plan: {
            ...currentAssetPlan,
            assets: (currentAssetPlan.assets || []).filter((_, index) => index !== assetIndex),
          },
        },
      };
    });
    setCreatedDraft(null);
    setCompositionResult(null);
    setAssetRequirements([]);
    setCreatedRequirementKeys(new Set());
    setStatusMessage("");
  };

  const applyStylePreset = (presetId) => {
    const preset = activeFamily.stylePresets.find((item) => item.id === presetId);
    setForm((current) => {
      if (!preset) return { ...current, style_preset_id: presetId };

      return {
        ...current,
        style_preset_id: preset.id,
        style_prompt: preset.prompt,
        mood_text: preset.mood,
        typography_direction: preset.typography,
        ornament_density: preset.ornamentDensity,
        palette_primary: preset.palette.primary,
        palette_secondary: preset.palette.secondary,
        palette_accent: preset.palette.accent,
        palette_background: preset.palette.background,
        palette_text_color: preset.palette.text,
        palette_text: [
          preset.label,
          preset.palette.primary,
          preset.palette.secondary,
          preset.palette.accent,
          preset.palette.background,
          preset.palette.text,
        ].join(", "),
      };
    });
  };

  const buildSelectedAssetForRole = (roleConfig, asset) => ({
    binding_scope: "template_role",
    asset_library_item_id: asset.id,
    asset_name: asset.name,
    role: roleConfig.role,
    usage_mode: "place_directly",
    instruction:
      roleConfig.placementSummary ||
      `Place this asset in the ${prettifyToken(roleConfig.role)} slot.`,
    target_scenes_text: (roleConfig.placement || [])
      .map((placement) => placement.sceneId)
      .filter(Boolean)
      .join(", "),
    strength: roleConfig.required ? "high" : "medium",
    placement: roleConfig.placement || [],
    slot: roleConfig.placement?.[0]?.slot || "",
    media_url: getAssetMediaUrl(asset),
    preview_url: getAssetPreviewUrl(asset),
  });

  const buildSelectedAssetForSceneSlot = (slot, roleConfig, asset) => ({
    ...buildSelectedAssetForRole(roleConfig, asset),
    binding_scope: "scene_slot",
    scene_id: slot.sceneId,
    scene_name: slot.sceneName,
    element_id: slot.elementId,
    slot_key: slot.key,
    role: slot.role,
    instruction:
      roleConfig.placementSummary ||
      `Place this asset in ${slot.sceneName || slot.sceneId} for ${prettifyToken(slot.role)}.`,
    target_scenes_text: slot.sceneId,
    placement: [
      {
        sceneId: slot.sceneId,
        slot: slot.elementId || slot.role,
        ...slot.placement,
      },
    ],
    slot: slot.elementId || slot.role,
  });

  const selectAssetForRole = (roleConfig, asset) => {
    setForm((current) => {
      const nextAsset = buildSelectedAssetForRole(roleConfig, asset);
      const selectedAssets = (current.selected_assets || []).filter(
        (selectedAsset) =>
          selectedAsset.role !== roleConfig.role &&
          String(selectedAsset.asset_library_item_id) !== String(asset.id)
      );
      const templateAssetBindings = getTemplateAssetBindings(current).filter(
        (binding) =>
          normalizeAssetText(binding.role) !== normalizeAssetText(roleConfig.role) &&
          String(binding.asset_library_item_id) !== String(asset.id)
      );

      return {
        ...current,
        template_asset_bindings: [...templateAssetBindings, nextAsset],
        selected_assets: [...selectedAssets, nextAsset],
      };
    });
    setCreatedDraft(null);
    setStatusTone("success");
    setStatusMessage("Asset selected for this role. Review the current plan or regenerate composition if you want AI to rebalance it.");
  };

  const selectAssetForSceneSlot = (slot, roleConfig, asset) => {
    setForm((current) => {
      const nextAsset = buildSelectedAssetForSceneSlot(slot, roleConfig, asset);
      const isDifferentSlot = (binding) =>
        binding.slot_key !== slot.key &&
        !(binding.scene_id === slot.sceneId && binding.element_id === slot.elementId);

      return {
        ...current,
        template_asset_bindings: [...getTemplateAssetBindings(current).filter(isDifferentSlot), nextAsset],
        selected_assets: [
          ...(current.selected_assets || []).filter(isDifferentSlot),
          nextAsset,
        ],
      };
    });
    setCreatedDraft(null);
    setStatusTone("success");
    setStatusMessage(
      `Asset selected for ${slot.sceneName || slot.sceneId}. Other scenes using ${prettifyToken(slot.role)} were not changed.`
    );
  };

  const clearAssetRole = (role) => {
    setForm((current) => ({
      ...current,
      template_asset_bindings: getTemplateAssetBindings(current).filter(
        (asset) => normalizeAssetText(asset.role) !== normalizeAssetText(role)
      ),
      selected_assets: (current.selected_assets || []).filter(
        (asset) => normalizeAssetText(asset.role) !== normalizeAssetText(role)
      ),
    }));
    setCreatedDraft(null);
    setStatusTone("idle");
    setStatusMessage("Asset role cleared. The current plan is still visible, but you may regenerate composition for best results.");
  };

  const clearSceneSlotAsset = (slot) => {
    const isDifferentSlot = (binding) =>
      binding.slot_key !== slot.key &&
      !(binding.scene_id === slot.sceneId && binding.element_id === slot.elementId);

    setForm((current) => ({
      ...current,
      template_asset_bindings: getTemplateAssetBindings(current).filter(isDifferentSlot),
      selected_assets: (current.selected_assets || []).filter(isDifferentSlot),
    }));
    setCreatedDraft(null);
    setStatusTone("idle");
    setStatusMessage(`Asset cleared for ${slot.sceneName || slot.sceneId}.`);
  };

  const useSuggestedRoleAssets = () => {
    setForm((current) => {
      const roleAssetPairs = activeFamily.generatableAssetRoles
        .map((roleConfig) => ({
          roleConfig,
          asset: assetRoleSuggestions.get(roleConfig.role),
        }))
        .filter((item) => item.asset);
      const selectedRoleSet = new Set(roleAssetPairs.map((item) => item.roleConfig.role));
      const selectedIdSet = new Set(roleAssetPairs.map((item) => String(item.asset.id)));
      const remainingAssets = (current.selected_assets || []).filter(
        (asset) =>
          !selectedRoleSet.has(asset.role) &&
          !selectedIdSet.has(String(asset.asset_library_item_id))
      );
      const remainingBindings = getTemplateAssetBindings(current).filter(
        (asset) =>
          !selectedRoleSet.has(asset.role) &&
          !selectedIdSet.has(String(asset.asset_library_item_id))
      );
      const nextRoleAssets = roleAssetPairs.map(({ roleConfig, asset }) =>
        buildSelectedAssetForRole(roleConfig, asset)
      );

      return {
        ...current,
        template_asset_bindings: [...remainingBindings, ...nextRoleAssets],
        selected_assets: [...remainingAssets, ...nextRoleAssets],
      };
    });
    setCreatedDraft(null);
    setStatusTone("success");
    setStatusMessage("Suggested assets selected. Review the current plan or regenerate composition if you want AI to rebalance it.");
  };

  const useV2SuggestedAssets = () => {
    setForm((current) => {
      const roleAssetPairs = v2AssetRoleConfigs
        .map((roleConfig) => ({
          roleConfig,
          asset: v2AssetRoleSuggestions.get(roleConfig.role),
        }))
        .filter((item) => item.asset);
      const selectedRoleSet = new Set(roleAssetPairs.map((item) => item.roleConfig.role));
      const selectedIdSet = new Set(roleAssetPairs.map((item) => String(item.asset.id)));
      const remainingAssets = (current.selected_assets || []).filter(
        (asset) =>
          !selectedRoleSet.has(asset.role) &&
          !selectedIdSet.has(String(asset.asset_library_item_id))
      );
      const remainingBindings = getTemplateAssetBindings(current).filter(
        (asset) =>
          !selectedRoleSet.has(asset.role) &&
          !selectedIdSet.has(String(asset.asset_library_item_id))
      );
      const nextRoleAssets = roleAssetPairs.map(({ roleConfig, asset }) =>
        buildSelectedAssetForRole(roleConfig, asset)
      );

      return {
        ...current,
        template_asset_bindings: [...remainingBindings, ...nextRoleAssets],
        selected_assets: [...remainingAssets, ...nextRoleAssets],
      };
    });
    setCreatedDraft(null);
    setStatusTone("success");
    setStatusMessage("Matched V2 planned assets from the library. Review the current plan or regenerate composition before building the draft.");
  };

  const createMissingV2AssetBriefs = async () => {
    const missingRoleSet = new Set(v2MissingLibraryRoles.map((roleConfig) => roleConfig.role));
    const plannedAssets = form.v2_brief?.asset_plan?.assets || [];
    const missingPlannedAssets = plannedAssets.filter((asset) =>
      missingRoleSet.has(asset.role || asset.asset_key)
    );

    if (!missingPlannedAssets.length) {
      setStatusTone("idle");
      setStatusMessage("No missing V2 asset briefs to create.");
      return;
    }

    setIsCreatingV2Briefs(true);
    setStatusTone("idle");
    setStatusMessage("Creating missing V2 asset briefs...");

    try {
      const createdAssets = await Promise.all(
        missingPlannedAssets.map((asset) => {
          const role = asset.role || asset.asset_key;
          const requirement = {
            id: asset.asset_key || role,
            role,
            asset_role: role,
            asset_group: asset.asset_group || "v2_planned",
            type: asset.transparent_background_required ? "transparent_png" : "image",
            description: asset.purpose || asset.prompt || `V2 planned asset for ${role}.`,
            generation_prompt: asset.prompt || asset.purpose || "",
            negative_prompt: asset.negative_prompt || "",
            target_scenes: asset.target_scenes || [],
            aspect_ratio: asset.aspect_ratio || "",
            intended_width: asset.intended_width,
            intended_height: asset.intended_height,
            usage_tags: [role, asset.asset_key, asset.source_strategy, ...(asset.target_scenes || [])].filter(Boolean),
            source: "v2_brief",
          };

          return createAssetLibraryItem(
            buildAssetBriefPayload({
              requirement,
              family: activeFamily,
              form,
              templateId: createdDraft?.templateId || null,
            })
          );
        })
      );

      setAssetLibraryItems((current) => [...createdAssets, ...current]);
      setStatusTone("success");
      setStatusMessage(
        `Created ${createdAssets.length} V2 asset brief${createdAssets.length === 1 ? "" : "s"}. Generate and review them in Asset Library.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create missing V2 asset briefs.");
    } finally {
      setIsCreatingV2Briefs(false);
    }
  };

  const createAssetBrief = async (requirement) => {
    const requirementKey = requirement?.id || getRequirementRole(requirement);
    setCreatingRequirementKey(requirementKey);
    setStatusTone("idle");
    setStatusMessage("Creating asset brief...");

    try {
      const asset = await createAssetLibraryItem(
        buildAssetBriefPayload({
          requirement,
          family: activeFamily,
          form,
          templateId: createdDraft?.templateId || null,
        })
      );
      setAssetLibraryItems((current) => [asset, ...current]);
      setCreatedRequirementKeys((current) => new Set([...current, requirementKey]));
      setStatusTone("success");
      setStatusMessage("Asset brief created in the library. Generate/review it from the asset workflow.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create asset brief.");
    } finally {
      setCreatingRequirementKey("");
    }
  };

  const openAssetLibraryWithRequirements = () => {
    const requirementMap = new Map();
    [...assetRequirements, ...compositionMissingRoleRequirements].forEach((requirement) => {
      const role = getRequirementRole(requirement);
      if (role && !requirementMap.has(role)) {
        requirementMap.set(role, requirement);
      }
    });
    const pendingBriefs = Array.from(requirementMap.values()).map((requirement) =>
      buildAssetBriefPayload({
        requirement,
        family: activeFamily,
        form,
        templateId: createdDraft?.templateId,
      })
    );

    const payload = {
      source: "admin_ai_template_creation",
      templateId: createdDraft?.templateId || null,
      templateName: form.template_name || activeFamily.label,
      familyId: activeFamily.id,
      createdAt: new Date().toISOString(),
      pendingBriefs,
    };

    window.sessionStorage.setItem("admin-asset-library-pending-briefs", JSON.stringify(payload));
    navigate("/admin/assets", { state: payload });
  };

  const buildAiTemplateRequest = () => {
    const assetLookup = new Map(assetLibraryItems.map((asset) => [String(asset.id), asset]));
    const legacyForm = stripV2Fields(form);
    const v2Brief = form.v2_brief || null;
    const selectedAssetsForRequest = (effectiveSelectedAssets || []).map((asset) => {
      const libraryAsset = assetLookup.get(String(asset.asset_library_item_id));

      return {
        binding_scope: asset.binding_scope || "template_role",
        asset_library_item_id: asset.asset_library_item_id,
        asset_name: asset.asset_name,
        role: asset.role,
        scene_id: asset.scene_id || "",
        scene_name: asset.scene_name || "",
        element_id: asset.element_id || "",
        slot_key: asset.slot_key || "",
        usage_mode: asset.usage_mode,
        instruction: asset.instruction,
        target_scenes: textToTags(asset.target_scenes_text || ""),
        target_scenes_text: asset.target_scenes_text || "",
        strength: asset.strength || "medium",
        placement: asset.placement || [],
        slot: asset.slot || "",
        media_url: libraryAsset ? getAssetMediaUrl(libraryAsset) : asset.media_url || "",
        preview_url: libraryAsset ? getAssetPreviewUrl(libraryAsset) : asset.preview_url || "",
        file_url: libraryAsset?.file_url || "",
        thumbnail_url: libraryAsset?.thumbnail_url || "",
        asset_type: libraryAsset?.asset_type || "",
        status: libraryAsset?.status || "",
        asset_group: libraryAsset?.asset_group || "",
        asset_role: libraryAsset?.asset_role || "",
        source_type: libraryAsset?.source_type || "",
      };
    });
    const availableAssetRoles = Array.from(
      new Set([...matchingLibraryRoles, ...v2AssetRoleSuggestions.keys()])
    );
    const palette = getCreativeBriefPalette(form);
    const request = {
      ...legacyForm,
      v2_brief: v2Brief,
      template_name: legacyForm.template_name.trim(),
      palette_text: legacyForm.palette_text.trim() || activeFamily.defaultForm?.palette_text || "",
      palette,
      selected_assets: selectedAssetsForRequest,
      available_asset_roles: availableAssetRoles,
      missing_asset_roles: [
        ...missingLibraryRoles.map((roleConfig) => roleConfig.role),
        ...v2MissingLibraryRoles.map((roleConfig) => roleConfig.role),
      ],
      scene_count: Number(legacyForm.scene_count) || activeFamily.defaultSceneCount,
    };

    return { request, legacyForm, selectedAssetsForRequest };
  };

  const generateCompositionPreview = async () => {
    if (!form.template_name.trim()) {
      setStatusTone("error");
      setStatusMessage("Template name is required.");
      return;
    }

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/admin/templates/ai-create" } });
      return;
    }

    setIsGeneratingComposition(true);
    setStatusTone("idle");
    setStatusMessage("Generating V3 composition plan...");

    try {
      const { request } = buildAiTemplateRequest();
      const result = await generateAiCompositionPlan(request);
      setCompositionResult(result);
      setAssetRequirements(result?.composition_plan?.asset_requirements || []);
      setStatusTone("success");
      setStatusMessage(
        `Composition plan ready from ${result?.source || "planner"}. Review it before building Polotno.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to generate V3 composition plan.");
    } finally {
      setIsGeneratingComposition(false);
    }
  };

  const refineCompositionPreview = async () => {
    if (!compositionPlan) {
      setStatusTone("error");
      setStatusMessage("Generate a V3 composition plan before refining.");
      return;
    }

    if (!compositionFeedback.trim()) {
      setStatusTone("error");
      setStatusMessage("Add feedback for the AI repair pass.");
      return;
    }

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/admin/templates/ai-create" } });
      return;
    }

    setIsRefiningComposition(true);
    setStatusTone("idle");
    setStatusMessage("Refining composition plan with AI critic/repair...");

    try {
      const { request } = buildAiTemplateRequest();
      const activeQuality = enrichedCompositionQuality || compositionQuality;
      const result = await refineAiCompositionPlan({
        request,
        composition_plan: compositionPlan,
        admin_feedback: compositionFeedback.trim(),
        quality_issues: activeQuality?.blockingIssues || [],
        quality_warnings: activeQuality?.warnings || [],
        asset_readiness: activeQuality?.assetReadiness || null,
      });
      setCompositionResult(result);
      setAssetRequirements(result?.composition_plan?.asset_requirements || []);
      setCreatedDraft(null);
      setStatusTone("success");
      setStatusMessage(
        `Composition refined from ${result?.source || "planner"}. Review the updated score before building draft.`
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to refine composition plan.");
    } finally {
      setIsRefiningComposition(false);
    }
  };

  const submit = async () => {
    if (!form.template_name.trim()) {
      setStatusTone("error");
      setStatusMessage("Template name is required.");
      return;
    }

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/admin/templates/ai-create" } });
      return;
    }

    setIsGenerating(true);
    setCreatedDraft(null);
    setStatusTone("idle");
    setStatusMessage(
      compositionPlan
        ? "Building local Polotno draft from V3 composition and scene asset mapping..."
        : "Generating creative brief plan..."
    );

    try {
      console.info("[AdminAiTemplateCreatePage] Generating AI template draft", {
        templateName: form.template_name,
        eventType: form.event_type,
        styleFamily: form.style_family,
        hasV2Brief: Boolean(form.v2_brief),
        draftSource: compositionPlan ? "v3_composition_scene_mapping" : "v2_template_plan_fallback",
      });
      const { request, legacyForm, selectedAssetsForRequest } = buildAiTemplateRequest();
      const palette = request.palette;
      const planningResult = compositionPlan
        ? {
            source: "v3_composition_scene_mapping",
            model: "local_polotno_draft_builder",
            plan: { asset_requirements: [] },
          }
        : await generateAiTemplatePlan(request);
      if (!compositionPlan) {
        console.info("[AdminAiTemplateCreatePage] AI template plan created", {
          source: planningResult?.source,
          model: planningResult?.model,
          assetRequirements: planningResult?.plan?.asset_requirements?.length || 0,
        });
      }
      const plan = planningResult?.plan;
      const requirements = Array.isArray(plan?.asset_requirements) ? plan.asset_requirements : [];
      const draftForm = {
        ...legacyForm,
        v2_brief: request.v2_brief,
        selected_assets: selectedAssetsForRequest,
      };
      const draftJson = compositionPlan
        ? compositionPlanToPolotnoDraft({
            compositionPlan,
            selectedAssets: selectedAssetsForRequest,
            family: activeFamily,
          })
        : buildTemplateDraft({ family: activeFamily, form: draftForm, plan });
      const draftAssetSources = (draftJson.pages || []).flatMap((page) =>
        (page.children || [])
          .filter((child) => ["image", "svg", "video"].includes(child.type))
          .map((child) => ({
            page: page.id,
            id: child.id,
            type: child.type,
            name: child.name,
            src: child.src,
          }))
      );
      const marigoldGarlandNodes = draftAssetSources.filter((asset) =>
        [asset.id, asset.name, asset.src]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes("marigold")
      );
      console.info("[AdminAiTemplateCreatePage] Generated draft asset sources", draftAssetSources);
      console.info("[AdminAiTemplateCreatePage] Generated marigold draft nodes", marigoldGarlandNodes);
      const planSourceTag =
        compositionPlan
          ? "v3_scene_mapped"
          : planningResult?.source === "openai"
          ? "openai_planned"
          : planningResult?.source === "gemini"
          ? "gemini_planned"
          : "fallback_planned";

      const templateMetadata = {
        name: form.template_name.trim(),
        category: activeFamily.category,
        event_type: activeFamily.eventType,
        htmltext: JSON.stringify(draftJson),
        tags: [...activeFamily.defaultTags, planSourceTag],
        style_tags: [
          ...activeFamily.defaultStyleTags,
          form.style_preset_id,
          ...textToTags(form.mood_text || ""),
        ].filter(Boolean),
        culture_tags: textToTags(form.cultural_context_text || ""),
        religion_tags: [],
        language_tags: [],
        subevent_types: activeFamily.defaultSubeventTypes,
        is_featured: Boolean(form.is_featured),
        is_premium: Boolean(form.is_premium),
      };
      const pendingTemplateAssets = aiAssetPlan.map((asset) => {
        const assetPayload = mapRegistryAssetToCreatePayload(asset);
        return {
          ...assetPayload,
          asset_metadata: {
            ...(assetPayload.asset_metadata || {}),
            creative_brief: {
              style_preset_id: form.style_preset_id,
              palette,
              selected_assets: selectedAssetsForRequest,
            },
          },
        };
      });
      const draftId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? `admin-draft-${crypto.randomUUID()}`
          : `admin-draft-${Date.now()}`;
      console.info("[AdminAiTemplateCreatePage] Unsaved template draft created", {
        draftId,
        pendingTemplateAssets: pendingTemplateAssets.length,
      });

      setStatusTone("success");
      setStatusMessage(
        compositionPlan
          ? `V3 draft created locally with ${
              selectedAssetsForRequest.filter((asset) => asset.binding_scope === "scene_slot").length
            } scene-slot binding(s) and ${marigoldGarlandNodes.length} marigold node(s). Open Polotno to review, then save only if approved.`
          : "Draft created locally. Review asset requirements, then open Polotno when ready."
      );
      setCreatedDraft({
        templateId: draftId,
        draftJson,
        templateMetadata,
        pendingTemplateAssets,
      });
      setAssetRequirements(requirements);
    } catch (error) {
      console.error("[AdminAiTemplateCreatePage] Unable to create AI-assisted template", error);
      setStatusTone("error");
      setStatusMessage(
        error?.message
          ? `Unable to create AI-assisted template: ${error.message}`
          : "Unable to create AI-assisted template."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const openCreatedDraft = () => {
    if (!createdDraft) {
      setStatusTone("error");
      setStatusMessage("Generate a draft before opening Polotno.");
      return;
    }

    openTemplateEditor({
      draftId: createdDraft.templateId,
      draftJson: createdDraft.draftJson,
      templateMetadata: createdDraft.templateMetadata,
      pendingTemplateAssets: createdDraft.pendingTemplateAssets,
    });
  };

  return (
    <div className="min-h-screen bg-[#fff8fb] text-[#251c25]">
      <section className="border-b border-[#f2dfea] bg-[linear-gradient(180deg,#fff8fb_0%,#fff2f8_100%)]">
        <div className="mx-auto w-full max-w-[1480px] px-4 py-8 md:px-8 xl:px-10">
          <button
            type="button"
            onClick={() => navigate("/admin/templates")}
            className="inline-flex items-center gap-2 rounded-full border border-[#efd9e4] bg-white px-4 py-2 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099]"
          >
            <ArrowLeft size={16} />
            Back to templates
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/assets")}
            className="ml-3 inline-flex items-center gap-2 rounded-full border border-[#efd9e4] bg-white px-4 py-2 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099]"
          >
            Asset Library
          </button>
          <div className="mt-6 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                Admin AI template creation
              </p>
              <h1 className="mt-3 font-[Georgia] text-[clamp(2rem,4vw,3.6rem)] font-black leading-tight text-[#241824]">
                Build a draft from a creative brief
              </h1>
              <p className="mt-4 text-base leading-7 text-[#6f5a67] md:text-lg">
                Choose the style, colors, and asset references. AI creates one editable
                draft, then Polotno stays in your hands for refinement.
              </p>
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={isGenerating}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Building draft...
                </>
              ) : (
                <>
                  {createdDraft ? "Rebuild Local Draft" : "Build Local Draft"}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={resetToFamilyDefaults}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#efd9e4] bg-white px-6 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
            >
              Reset to Family Defaults
            </button>
            <button
              type="button"
              onClick={clearSavedWorkspace}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#efd9e4] bg-white/70 px-6 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
            >
              Clear Workspace
            </button>
          </div>
          {statusMessage ? (
            <div
              className={`mt-5 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                statusTone === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : statusTone === "success"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-[#efd9e4] bg-white text-[#735f6d]"
              }`}
            >
              {statusMessage}
            </div>
          ) : null}
        </div>
      </section>

      <main className="mx-auto grid w-full max-w-[1480px] gap-6 px-4 py-8 md:px-8 xl:grid-cols-[minmax(0,1fr)_360px] xl:px-10">
        <div className="space-y-6">
          <section className="rounded-[32px] border border-[#dbeafe] bg-[#f8fbff] p-5 shadow-sm md:p-6">
            <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#3970a8]">
                  <PencilRuler size={16} />
                  V3 composition plan
                </div>
                <p className="mt-2 text-sm leading-6 text-[#4f6f9b]">
                  Generate a planning-only scene composition before building Polotno. This lets us
                  inspect layers, placements, missing assets, and animations without saving a
                  template.
                </p>
              </div>
              <button
                type="button"
                onClick={generateCompositionPreview}
                disabled={isGeneratingComposition}
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#153d6d] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isGeneratingComposition ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Planning...
                  </>
                ) : (
                  "Generate Composition Plan"
                )}
              </button>
            </div>

            <div
              className={`mt-5 rounded-[24px] border p-4 ${
                compositionPlan
                  ? "border-[#cfe9d8] bg-[#f4fbf6]"
                  : "border-[#f1d8a4] bg-[#fff9e9]"
              }`}
            >
              <p
                className={`text-sm font-black ${
                  compositionPlan ? "text-[#25733f]" : "text-[#7c5412]"
                }`}
              >
                Draft source: {compositionPlan ? "V3 composition + scene asset mapping" : "template defaults fallback"}
              </p>
              <p className="mt-2 text-xs leading-5 text-[#4f6f9b]">
                {compositionPlan
                  ? "Generate Draft will use the V3 composition plan and the current Scene Asset Mapping bindings. Template setup defaults are only upstream context unless you regenerate the V3 plan."
                  : "No V3 composition plan exists yet. Generate Draft will fall back to template defaults and selected assets."}
              </p>
            </div>

            {assetReadiness ? (
              <div
                className={`mt-5 rounded-[24px] border p-4 ${
                  assetReadiness.state === "premium_ready"
                    ? "border-[#cfe9d8] bg-[#f4fbf6]"
                    : assetReadiness.state === "starter_ready"
                    ? "border-[#f1d8a4] bg-[#fff9e9]"
                    : "border-[#f1c4c4] bg-[#fff6f6]"
                }`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <p className="text-sm font-black text-[#153d6d]">
                      Asset readiness gate: {assetReadiness.state.replace(/_/g, " ")}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                      {assetReadiness.score}/100 readiness, {assetReadiness.selectedCount} selected
                      asset roles, {assetReadiness.requiredCount} required roles.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={useSuggestedRoleAssets}
                    disabled={!assetRoleSuggestions.size}
                    className="inline-flex shrink-0 items-center justify-center rounded-full bg-white px-4 py-2 text-xs font-bold text-[#153d6d] transition-colors hover:bg-[#153d6d] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Use suggested assets
                  </button>
                </div>

                {assetReadiness.missingRequired.length ? (
                  <div className="mt-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#8a2d2d]">
                      Required before premium generation
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {assetReadiness.missingRequired.map((roleConfig) => (
                        <div
                          key={roleConfig.role}
                          className="inline-flex overflow-hidden rounded-full bg-white text-xs font-bold text-[#8a2d2d] shadow-sm"
                        >
                          <button
                            type="button"
                            onClick={() => focusAssetRole(roleConfig)}
                            className="px-3 py-1.5 transition-colors hover:bg-[#8a2d2d] hover:text-white"
                          >
                            {roleConfig.name || prettifyToken(roleConfig.role)}
                          </button>
                          <button
                            type="button"
                            onClick={() => createAssetBriefForRole(roleConfig, "asset_readiness_required")}
                            disabled={creatingRequirementKey === roleConfig.role}
                            className="border-l border-red-100 px-3 py-1.5 transition-colors hover:bg-[#8a2d2d] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {creatingRequirementKey === roleConfig.role ? "Creating..." : "Create brief"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {assetReadiness.missingRecommended.length ? (
                  <div className="mt-4">
                    <p className="text-xs font-black uppercase tracking-[0.14em] text-[#7c5412]">
                      Recommended for richer drafts
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {assetReadiness.missingRecommended.slice(0, 8).map((roleConfig) => (
                        <div
                          key={roleConfig.role}
                          className="inline-flex overflow-hidden rounded-full bg-white text-xs font-bold text-[#7c5412] shadow-sm"
                        >
                          <button
                            type="button"
                            onClick={() => focusAssetRole(roleConfig)}
                            className="px-3 py-1.5 transition-colors hover:bg-[#7c5412] hover:text-white"
                          >
                            {roleConfig.name || prettifyToken(roleConfig.role)}
                          </button>
                          <button
                            type="button"
                            onClick={() => createAssetBriefForRole(roleConfig, "asset_readiness_recommended")}
                            disabled={creatingRequirementKey === roleConfig.role}
                            className="border-l border-amber-100 px-3 py-1.5 transition-colors hover:bg-[#7c5412] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {creatingRequirementKey === roleConfig.role ? "Creating..." : "Create brief"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {compositionPlan ? (
              <div className="mt-5 space-y-4">
                <div className="grid gap-3 md:grid-cols-5">
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                      Source
                    </p>
                    <p className="mt-1 text-sm font-bold text-[#153d6d]">
                      {compositionResult?.source || "planner"}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                      Scenes
                    </p>
                    <p className="mt-1 text-sm font-bold text-[#153d6d]">
                      {compositionScenes.length}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                      Elements
                    </p>
                    <p className="mt-1 text-sm font-bold text-[#153d6d]">
                      {compositionScenes.reduce(
                        (total, scene) => total + (scene.elements?.length || 0),
                        0
                      )}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                      Missing
                    </p>
                    <p className="mt-1 text-sm font-bold text-[#153d6d]">
                      {compositionMissingRoles.length} assets
                    </p>
                  </div>
                  <div className="rounded-2xl bg-white px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                      Quality
                    </p>
                    <p className="mt-1 text-sm font-bold text-[#153d6d]">
                      {enrichedCompositionQuality?.score ?? compositionQuality?.score ?? 0}/100
                    </p>
                  </div>
                </div>

                <div className="rounded-[24px] border border-[#dbeafe] bg-white p-4">
                  <p className="text-sm font-black text-[#153d6d]">Composition intent</p>
                  <p className="mt-2 text-sm leading-6 text-[#4f6f9b]">
                    {compositionPlan.composition_summary?.design_intent}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-[#4f6f9b]">
                    {compositionPlan.composition_summary?.readability_strategy}
                  </p>
                </div>

                {compositionQuality ? (
                  <div
                    className={`rounded-[24px] border p-4 ${
                      enrichedCompositionQuality?.status === "strong"
                        ? "border-[#cfe9d8] bg-[#f4fbf6]"
                        : enrichedCompositionQuality?.status === "starter"
                        ? "border-[#f1d8a4] bg-[#fff9e9]"
                        : "border-[#f1c4c4] bg-[#fff6f6]"
                    }`}
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="text-sm font-black text-[#153d6d]">
                          Draft quality after recipes: {enrichedCompositionQuality?.score ?? compositionQuality.score}/100
                        </p>
                        <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                          Raw AI plan: {compositionQuality.score}/100
                          {enrichedCompositionQuality
                            ? `, recipe-enriched: ${enrichedCompositionQuality.score}/100`
                            : ""}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                          Asset readiness: {compositionQuality.assetReadiness.state.replace(/_/g, " ")} -{" "}
                          {compositionQuality.assetReadiness.score}/100
                        </p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#153d6d]">
                        {(enrichedCompositionQuality?.status || compositionQuality.status).replace(/_/g, " ")}
                      </span>
                    </div>
                    {(enrichedCompositionQuality?.blockingIssues || compositionQuality.blockingIssues).length ? (
                      <div className="mt-3 space-y-1">
                        {(enrichedCompositionQuality?.blockingIssues || compositionQuality.blockingIssues)
                          .slice(0, 4)
                          .map((issue, index) => (
                          <p key={`${issue}-${index}`} className="text-xs leading-5 text-[#8a2d2d]">
                            {issue}
                          </p>
                        ))}
                      </div>
                    ) : null}
                    {!(enrichedCompositionQuality?.blockingIssues || compositionQuality.blockingIssues).length &&
                    (enrichedCompositionQuality?.warnings || compositionQuality.warnings).length ? (
                      <div className="mt-3 space-y-1">
                        {(enrichedCompositionQuality?.warnings || compositionQuality.warnings)
                          .slice(0, 4)
                          .map((warning, index) => (
                          <p key={`${warning}-${index}`} className="text-xs leading-5 text-[#7c5412]">
                            {warning}
                          </p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="rounded-[24px] border border-[#dbeafe] bg-white p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-sm font-black text-[#153d6d]">Refine with AI</p>
                      <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                        Give the critic/repair agent specific feedback. It will revise the V3
                        composition plan without saving or changing assets.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={refineCompositionPreview}
                      disabled={isRefiningComposition || !compositionFeedback.trim()}
                      className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#153d6d] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isRefiningComposition ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Refining...
                        </>
                      ) : (
                        "Refine Plan"
                      )}
                    </button>
                  </div>
                  <textarea
                    value={compositionFeedback}
                    onChange={(event) => setCompositionFeedback(event.target.value)}
                    rows={3}
                    placeholder="Example: Make the opening richer, move Ganesha lower, add more marigold accents on detail pages, and make the venue page less plain."
                    className="mt-3 w-full rounded-2xl border border-[#c7dff7] bg-[#f8fbff] px-4 py-3 text-sm leading-6 outline-none transition-colors focus:border-[#ff0099]"
                  />
                </div>

                {compositionWarnings.length ? (
                  <div className="rounded-[24px] border border-[#f1d8a4] bg-[#fff9e9] p-4">
                    <p className="text-sm font-black text-[#7c5412]">Warnings</p>
                    <div className="mt-2 space-y-1">
                      {compositionWarnings.map((warning, index) => (
                        <p key={`${warning}-${index}`} className="text-xs leading-5 text-[#7c5412]">
                          {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-[28px] border border-[#dbeafe] bg-[#f8fbff] p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="text-sm font-black text-[#153d6d]">Scene asset mapping</p>
                      <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                        Confirm which library asset is bound to each scene slot before generating
                        the Polotno draft. Changes here are scene-slot specific, so the same role
                        can use different assets in different scenes.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#153d6d]">
                        {sceneAssetMappingStats.mapped}/{sceneAssetMappingStats.total} mapped
                      </span>
                      {sceneAssetMappingStats.missing ? (
                        <span className="rounded-full bg-[#fff3d1] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#7c5412]">
                          {sceneAssetMappingStats.missing} missing
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {sceneAssetMappingStats.total ? (
                    <div className="mt-4 space-y-4">
                      {sceneAssetMappings.map(({ scene, sceneIndex, slots }) => (
                        <div
                          key={scene.scene_id || scene.page_id || sceneIndex}
                          className="rounded-[24px] border border-[#c7dff7] bg-white p-4"
                        >
                          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                            <div>
                              <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#3970a8]">
                                Scene {sceneIndex + 1}
                              </p>
                              <h3 className="mt-1 text-base font-black text-[#153d6d]">
                                {scene.name || scene.scene_id || `Scene ${sceneIndex + 1}`}
                              </h3>
                            </div>
                            <span className="w-fit rounded-full bg-[#eff6ff] px-3 py-1 text-[11px] font-bold text-[#2563a7]">
                              {slots.length} asset slot{slots.length === 1 ? "" : "s"}
                            </span>
                          </div>

                          {slots.length ? (
                            <div className="mt-4 grid gap-3">
                              {slots.map((slot) => {
                                const resolved = resolveSceneSlotAsset(slot);
                                const candidateAssets = getRoleCandidateAssets(slot.role);
                                const selectedId =
                                  resolved.source === "selected_by_admin" || resolved.source === "composition_binding"
                                    ? resolved.selectedAsset?.asset_library_item_id || resolved.libraryAsset?.id || ""
                                    : "";
                                const dropdownAssets =
                                  resolved.libraryAsset &&
                                  !candidateAssets.some((asset) => String(asset.id) === String(resolved.libraryAsset.id))
                                    ? [resolved.libraryAsset, ...candidateAssets]
                                    : candidateAssets;
                                const roleConfig = {
                                  role: slot.role,
                                  group: slot.role.split("_")[0],
                                  placementSummary:
                                    slot.placement?.safe_area_role ||
                                    slot.placement?.fit ||
                                    `${slot.role} in ${scene.name || slot.sceneId}`,
                                  placement: [
                                    {
                                      sceneId: slot.sceneId,
                                      slot: slot.placement?.safe_area_role || slot.elementId || slot.role,
                                      mode: slot.placement?.fit || "contain",
                                      x: slot.placement?.x,
                                      y: slot.placement?.y,
                                      width: slot.placement?.width,
                                      height: slot.placement?.height,
                                      opacity: 1,
                                    },
                                  ],
                                };

                                return (
                                  <div
                                    key={slot.key}
                                    className={`rounded-[20px] border p-3 ${
                                      resolved.source === "missing"
                                        ? "border-[#f1d8a4] bg-[#fff9e9]"
                                        : resolved.source === "selected_by_admin"
                                        ? "border-[#cfe9d8] bg-[#f4fbf6]"
                                        : "border-[#e2eaf5] bg-[#fbfdff]"
                                    }`}
                                  >
                                    <div className="flex flex-col gap-3 md:flex-row md:items-start">
                                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl border border-[#dbeafe] bg-white">
                                        {resolved.previewUrl ? (
                                          <img
                                            src={resolved.previewUrl}
                                            alt=""
                                            className="h-full w-full object-contain"
                                          />
                                        ) : (
                                          <span className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] font-black uppercase tracking-[0.12em] text-[#7c5412]">
                                            Missing
                                          </span>
                                        )}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                                          <div className="min-w-0">
                                            <p className="text-sm font-black text-[#153d6d]">
                                              {prettifyToken(slot.role)}
                                            </p>
                                            <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                                              Element: {slot.elementId || "asset layer"} · Source:{" "}
                                              {resolved.source.replace(/_/g, " ")}
                                            </p>
                                            <p className="mt-1 truncate text-xs font-bold text-[#315f91]">
                                              {resolved.name}
                                            </p>
                                          </div>
                                          <span
                                            className={`w-fit rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                                              resolved.source === "selected_by_admin"
                                                ? "bg-white text-[#25733f]"
                                                : resolved.source === "missing"
                                                ? "bg-white text-[#7c5412]"
                                                : "bg-[#eff6ff] text-[#2563a7]"
                                            }`}
                                          >
                                            {resolved.status?.replace(/_/g, " ") || "unknown"}
                                          </span>
                                        </div>

                                        <div className="mt-3 grid gap-2 lg:grid-cols-[minmax(0,1fr)_auto]">
                                          <select
                                            value={selectedId ? String(selectedId) : ""}
                                            onChange={(event) => {
                                              const nextId = event.target.value;
                                              if (!nextId) {
                                                clearSceneSlotAsset(slot);
                                                return;
                                              }
                                              const asset = assetLibraryById.get(String(nextId));
                                              if (asset) {
                                                selectAssetForSceneSlot(slot, roleConfig, asset);
                                              }
                                            }}
                                            className="min-w-0 rounded-2xl border border-[#c7dff7] bg-white px-3 py-2 text-xs font-bold text-[#153d6d] outline-none transition-colors focus:border-[#ff0099]"
                                          >
                                            <option value="">
                                              {candidateAssets.length ? "Choose role-matched asset" : "No role matches found"}
                                            </option>
                                            {dropdownAssets.slice(0, 24).map((asset) => (
                                              <option key={asset.id} value={asset.id}>
                                                {asset.name} {asset.status ? `(${asset.status})` : ""}
                                              </option>
                                            ))}
                                          </select>
                                          <div className="flex flex-wrap gap-2">
                                            {resolved.source !== "missing" ? (
                                              <button
                                                type="button"
                                                onClick={() => clearSceneSlotAsset(slot)}
                                                className="rounded-full border border-[#c7dff7] bg-white px-3 py-2 text-xs font-bold text-[#4f6f9b] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                                              >
                                                Clear
                                              </button>
                                            ) : null}
                                            <button
                                              type="button"
                                              onClick={() => focusAssetRole(roleConfig)}
                                              className="rounded-full border border-[#c7dff7] bg-white px-3 py-2 text-xs font-bold text-[#4f6f9b] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                                            >
                                              Find
                                            </button>
                                            {resolved.libraryAsset?.id ? (
                                              <button
                                                type="button"
                                                onClick={() => navigate("/admin/assets")}
                                                className="rounded-full border border-[#c7dff7] bg-white px-3 py-2 text-xs font-bold text-[#4f6f9b] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                                              >
                                                View library
                                              </button>
                                            ) : null}
                                            {resolved.source === "missing" ? (
                                              <button
                                                type="button"
                                                onClick={() => createAssetBriefForRole(roleConfig, "scene_asset_mapping")}
                                                disabled={creatingRequirementKey === slot.role}
                                                className="rounded-full bg-[#7c5412] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                                              >
                                                {creatingRequirementKey === slot.role ? "Creating..." : "Create brief"}
                                              </button>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="mt-3 rounded-2xl bg-[#f8fbff] px-3 py-3 text-xs leading-5 text-[#4f6f9b]">
                              This scene currently has no image, SVG, or video asset slots.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-4 rounded-2xl border border-dashed border-[#bdd7f4] bg-white px-4 py-4 text-sm leading-6 text-[#4f6f9b]">
                      No asset slots were found in this composition plan yet.
                    </p>
                  )}
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <div className="rounded-[24px] border border-[#cfe9d8] bg-[#f4fbf6] p-4">
                    <p className="text-sm font-black text-[#25733f]">Current mapped roles</p>
                    {getTemplateAssetBindings(form).length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {getTemplateAssetBindings(form).map((asset) => (
                          <span
                            key={`${asset.role}-${asset.asset_library_item_id}`}
                            className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#25733f]"
                          >
                            {asset.role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-xs leading-5 text-[#4b7a5b]">
                        No current role bindings are selected yet. Map assets in Scene Asset
                        Mapping before generating the draft.
                      </p>
                    )}
                  </div>
                  <div className="rounded-[24px] border border-[#f1d8a4] bg-[#fff9e9] p-4">
                    <p className="text-sm font-black text-[#7c5412]">New or missing roles</p>
                    {compositionMissingRoleRequirements.length ? (
                      <div className="mt-3 space-y-2">
                        {compositionMissingRoleRequirements.map((requirement, index) => {
                          const role = getRequirementRole(requirement);
                          const requirementKey = requirement.id || `${role}-${index}`;
                          const isCreated = createdRequirementKeys.has(requirementKey);

                          return (
                            <div
                              key={requirementKey}
                              className="flex flex-col gap-2 rounded-2xl bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="text-xs font-black uppercase tracking-[0.12em] text-[#7c5412]">
                                  {role}
                                </p>
                                <p className="mt-1 line-clamp-2 text-xs leading-5 text-[#8a6b30]">
                                  {requirement.description || requirement.generation_prompt || "Reusable asset needed before final draft."}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => createAssetBrief(requirement)}
                                disabled={isCreated || creatingRequirementKey === requirementKey}
                                className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#7c5412] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isCreated
                                  ? "Brief created"
                                  : creatingRequirementKey === requirementKey
                                  ? "Creating..."
                                  : "Create brief"}
                              </button>
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={openAssetLibraryWithRequirements}
                          className="mt-2 inline-flex w-full items-center justify-center rounded-full border border-[#d9a84d] bg-white px-4 py-2 text-xs font-bold text-[#7c5412] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                        >
                          Open asset library
                        </button>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs leading-5 text-[#7c5412]">
                        No new asset roles proposed.
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  {compositionScenes.map((scene, index) => (
                    <div
                      key={scene.page_id || scene.scene_id || index}
                      className="rounded-[24px] border border-[#dbeafe] bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.14em] text-[#3970a8]">
                            Scene {index + 1} - {scene.visual_density || "balanced"}
                          </p>
                          <h3 className="mt-1 text-lg font-black text-[#153d6d]">
                            {scene.name || scene.scene_id}
                          </h3>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <span className="rounded-full bg-[#eff6ff] px-3 py-1 text-[11px] font-bold text-[#2563a7]">
                            {enrichedCompositionPlan?.scenes?.[index]?.elements?.length || scene.elements?.length || 0} elements
                          </span>
                          {compositionQuality?.sceneScores?.[index] ? (
                            <span className="rounded-full bg-[#f8fbff] px-3 py-1 text-[11px] font-bold text-[#4f6f9b]">
                              {enrichedCompositionQuality?.sceneScores?.[index]?.score || compositionQuality.sceneScores[index].score}/100
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <p className="mt-2 text-xs leading-5 text-[#4f6f9b]">
                        {scene.layout_notes || scene.background?.notes || "No layout notes returned."}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(scene.elements || []).slice(0, 8).map((element) => (
                          <span
                            key={element.id}
                            className="rounded-full bg-[#f2f7ff] px-3 py-1 text-[11px] font-bold text-[#4f6f9b]"
                          >
                            {element.role}
                          </span>
                        ))}
                      </div>
                      {enrichedCompositionPlan?.scenes?.[index]?.custom?.scene_recipe_additions?.length ? (
                        <div className="mt-3 rounded-2xl bg-[#fff8ec] px-3 py-2">
                          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#9a6a10]">
                            Recipe additions
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {enrichedCompositionPlan.scenes[index].custom.scene_recipe_additions.map((role) => (
                              <span
                                key={`${scene.scene_id || index}-${role}`}
                                className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#9a6a10]"
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                <details className="rounded-[24px] border border-[#dbeafe] bg-[#0f2747] p-4 text-white">
                  <summary className="cursor-pointer text-sm font-black">
                    Raw V3 JSON
                  </summary>
                  <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap text-xs leading-5 text-[#dbeafe]">
                    {JSON.stringify(compositionPlan, null, 2)}
                  </pre>
                </details>
                {enrichedCompositionPlan ? (
                  <details className="rounded-[24px] border border-[#f1d8a4] bg-[#3f2d1d] p-4 text-white">
                    <summary className="cursor-pointer text-sm font-black">
                      Recipe-Enriched JSON
                    </summary>
                    <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap text-xs leading-5 text-[#fff3d1]">
                      {JSON.stringify(enrichedCompositionPlan, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : (
              <div className="mt-5 rounded-[24px] border border-dashed border-[#bdd7f4] bg-white/70 p-5 text-sm leading-6 text-[#4f6f9b]">
                No V3 plan generated yet. Review the family defaults, select or create assets, then
                generate a composition plan to inspect AI layout quality.
              </div>
            )}
          </section>

          <section className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                  <Sparkles size={16} />
                  Basics
                </div>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  Start from family defaults, then adjust the brief. Defaults are generic and
                  come from the selected event, style, and format.
                </p>
              </div>
              <button
                type="button"
                onClick={resetToFamilyDefaults}
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#251a24] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099]"
              >
                Reset to Family Defaults
              </button>
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Template Name</span>
                <input
                  value={form.template_name}
                  onChange={(event) => updateField("template_name", event.target.value)}
                  placeholder="Emerald Royal Wedding Invitation"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Template Family</span>
                <select
                  value={selectedFamilyId}
                  onChange={(event) => changeTemplateFamily(event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  {familyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs leading-5 text-[#8d6d7f]">{activeFamily.description}</p>
              </label>
              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Event Type</span>
                <select
                  value={form.event_type}
                  onChange={(event) => updateField("event_type", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  <option value={activeFamily.eventType}>{activeFamily.eventTypeLabel}</option>
                </select>
              </label>
              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Scene Count</span>
                <input
                  type="number"
                  min={Math.min(...activeFamily.supportedSceneCounts)}
                  max={Math.max(...activeFamily.supportedSceneCounts)}
                  value={form.scene_count}
                  onChange={(event) => updateField("scene_count", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
                <p className="text-xs leading-5 text-[#8d6d7f]">
                  {activeFamily.supportedSceneCounts.join(", ")} scene draft supported for this family.
                </p>
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Asset Requirement Mode</span>
                <select
                  value={form.asset_creation_mode}
                  onChange={(event) => updateField("asset_creation_mode", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  <option value="reuse_only">Use selected/existing assets only</option>
                  <option value="generate_missing">Suggest missing reusable assets</option>
                  <option value="generate_all">Suggest a fresh reusable asset set</option>
                </select>
                <p className="text-xs leading-5 text-[#8d6d7f]">
                  This only asks AI to describe needed assets. Asset creation and generation now
                  happen from the dedicated asset library workflow.
                </p>
              </label>
            </div>
          </section>

          <section className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
              <Palette size={16} />
              Style & palette
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Style Preset</span>
                <select
                  value={form.style_preset_id}
                  onChange={(event) => applyStylePreset(event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  {activeFamily.stylePresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Style Direction</span>
                <textarea
                  value={form.style_prompt}
                  onChange={(event) => updateField("style_prompt", event.target.value)}
                  rows={4}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>
              {[
                ["palette_primary", "Primary"],
                ["palette_secondary", "Secondary"],
                ["palette_accent", "Accent"],
                ["palette_background", "Background"],
                ["palette_text_color", "Text"],
              ].map(([field, label]) => (
                <label key={field} className="space-y-2">
                  <span className="text-sm font-bold text-[#4d3040]">{label} Color</span>
                  <div className="flex items-center gap-2 rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 focus-within:border-[#ff0099]">
                    <input
                      type="color"
                      value={form[field]}
                      onChange={(event) => updateField(field, event.target.value)}
                      className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                    />
                    <input
                      value={form[field]}
                      onChange={(event) => updateField(field, event.target.value)}
                      className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
                    />
                  </div>
                </label>
              ))}
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Palette Summary</span>
                <input
                  value={form.palette_text}
                  onChange={(event) => updateField("palette_text", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Mood</span>
                <input
                  value={form.mood_text}
                  onChange={(event) => updateField("mood_text", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>
              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Ornament Density</span>
                <select
                  value={form.ornament_density}
                  onChange={(event) => updateField("ornament_density", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  <option value="minimal">Minimal</option>
                  <option value="balanced">Balanced</option>
                  <option value="rich">Rich</option>
                  <option value="ornate">Ornate</option>
                </select>
              </label>
            </div>
          </section>

          {v2AssetRoleConfigs.length ? (
            <details className="rounded-[32px] border border-[#dbeafe] bg-[#f8fbff] p-5 shadow-sm md:p-6">
              <summary className="cursor-pointer list-none">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-black text-[#153d6d]">Advanced V2 role matches</p>
                    <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                      Optional compatibility helper. V3 Scene Asset Mapping is the draft source once V3 exists.
                    </p>
                  </div>
                  <span className="w-fit rounded-full bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#153d6d]">
                    Advanced
                  </span>
                </div>
              </summary>
              <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#3970a8]">
                    <Sparkles size={16} />
                    Advanced V2 role matches
                  </div>
                  <p className="mt-2 text-sm leading-6 text-[#4f6f9b]">
                    Optional compatibility helper for smart-default roles. When a V3 composition
                    exists, the Scene Asset Mapping panel is the source of truth for draft assets.
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={createMissingV2AssetBriefs}
                    disabled={!v2MissingLibraryRoles.length || isCreatingV2Briefs}
                    className="inline-flex shrink-0 items-center justify-center rounded-full border border-[#bdd7f4] bg-white px-4 py-2.5 text-xs font-bold text-[#153d6d] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isCreatingV2Briefs ? "Creating briefs..." : "Create missing briefs"}
                  </button>
                  <button
                    type="button"
                    onClick={useV2SuggestedAssets}
                    disabled={!v2AssetRoleSuggestions.size}
                    className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#153d6d] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Use V2 matches
                  </button>
                </div>
              </div>

              <div className="mt-5 grid gap-4 lg:grid-cols-2">
                {v2AssetRoleConfigs.map((roleConfig) => {
                  const suggestedAsset = v2AssetRoleSuggestions.get(roleConfig.role);
                  const selectedAsset = selectedAssetsByRole.get(roleConfig.role);
                  const librarySelectedAsset = selectedAsset
                    ? assetLibraryItems.find(
                        (asset) => String(asset.id) === String(selectedAsset.asset_library_item_id)
                      )
                    : null;

                  return (
                    <div
                      key={roleConfig.role}
                      className="rounded-[24px] border border-[#dbeafe] bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-black text-[#153d6d]">
                            {roleConfig.name}
                          </p>
                          <p className="mt-1 text-xs leading-5 text-[#4f6f9b]">
                            {roleConfig.placementSummary}
                          </p>
                        </div>
                        {selectedAsset ? (
                          <span className="rounded-full bg-[#e8f7ee] px-3 py-1 text-[11px] font-bold text-[#25733f]">
                            selected
                          </span>
                        ) : suggestedAsset ? (
                          <span className="rounded-full bg-[#eff6ff] px-3 py-1 text-[11px] font-bold text-[#2563a7]">
                            match found
                          </span>
                        ) : (
                          <span className="rounded-full bg-[#fff5e7] px-3 py-1 text-[11px] font-bold text-[#9a6a10]">
                            missing
                          </span>
                        )}
                      </div>

                      {librarySelectedAsset || suggestedAsset ? (
                        <div className="mt-4 flex gap-3 rounded-2xl border border-[#e4efff] bg-[#f8fbff] p-3">
                          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-white">
                            <img
                              src={getAssetPreviewUrl(librarySelectedAsset || suggestedAsset)}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold text-[#153d6d]">
                              {(librarySelectedAsset || suggestedAsset).name}
                            </p>
                            <p className="mt-1 text-xs text-[#4f6f9b]">
                              {(librarySelectedAsset || suggestedAsset).asset_group || "library"} /{" "}
                              {(librarySelectedAsset || suggestedAsset).status || "unknown"}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {suggestedAsset && !selectedAsset ? (
                                <button
                                  type="button"
                                  onClick={() => selectAssetForRole(roleConfig, suggestedAsset)}
                                  className="rounded-full bg-[#153d6d] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099]"
                                >
                                  Use this asset
                                </button>
                              ) : null}
                              {selectedAsset ? (
                                <button
                                  type="button"
                                  onClick={() => clearAssetRole(roleConfig.role)}
                                  className="rounded-full border border-[#dbeafe] bg-white px-3 py-1.5 text-xs font-bold text-[#4f6f9b] transition-colors hover:border-red-300 hover:text-red-600"
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 rounded-2xl border border-dashed border-[#dbeafe] bg-white/70 px-3 py-3">
                          <p className="text-xs leading-5 text-[#4f6f9b]">
                            No usable library asset matched this role yet. Create a reusable asset
                            brief, generate/review it in Asset Library, then return here.
                          </p>
                          <button
                            type="button"
                            onClick={() => createAssetBriefForRole(roleConfig, "v2_missing_match")}
                            disabled={creatingRequirementKey === roleConfig.role}
                            className="mt-3 inline-flex items-center justify-center rounded-full bg-[#153d6d] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {creatingRequirementKey === roleConfig.role ? "Creating..." : "Create brief"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          ) : null}

          <details className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-black text-[#4d3040]">Advanced V2 scene brief</p>
                  <p className="mt-1 text-xs leading-5 text-[#735f6d]">
                    Smart-default context used to generate or regenerate V3 composition.
                  </p>
                </div>
                <span className="w-fit rounded-full bg-[#fff8fb] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#7f2556]">
                  Advanced
                </span>
              </div>
            </summary>
            <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                  <Sparkles size={16} />
                  Advanced V2 scene brief
                </div>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  Smart-default context used before V3 composition is generated. Once V3 exists,
                  edits here require regenerating the V3 composition before they affect the draft.
                </p>
              </div>
              {!form.v2_brief ? (
                <button
                  type="button"
                  onClick={resetToFamilyDefaults}
                  className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#251a24] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099]"
                >
                  Reset to Family Defaults
                </button>
              ) : null}
            </div>

            {form.v2_brief ? (
              <div className="mt-5 space-y-5">
                {(form.v2_brief.scene_briefs || []).map((scene, sceneIndex) => (
                  <details
                    key={scene.scene_id || sceneIndex}
                    open={sceneIndex === 0}
                    className="rounded-[26px] border border-[#ecd2df] bg-[#fffafd] p-4"
                  >
                    <summary className="cursor-pointer list-none">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#a66f8c]">
                            Scene {sceneIndex + 1} - {scene.duration_seconds || 4}s - {scene.readability_level || "medium"} readability
                          </p>
                          <h3 className="mt-1 text-xl font-black text-[#251a24]">
                            {scene.scene_name || "Untitled scene"}
                          </h3>
                          <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                            {scene.animation_direction || scene.visual_direction || "Add scene direction."}
                          </p>
                        </div>
                        <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#7f2556]">
                          {(scene.asset_roles || []).length} assets - {(scene.element_motions || []).length} motions
                        </span>
                      </div>
                    </summary>

                    <div className="mt-5 grid gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Scene Name</span>
                        <input
                          value={scene.scene_name || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "scene_name", event.target.value)
                          }
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Duration Seconds</span>
                        <input
                          type="number"
                          min="1"
                          step="0.5"
                          value={scene.duration_seconds || 4}
                          onChange={(event) =>
                            updateV2SceneField(
                              sceneIndex,
                              "duration_seconds",
                              Number(event.target.value) || 1
                            )
                          }
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Purpose</span>
                        <textarea
                          value={scene.purpose || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "purpose", event.target.value)
                          }
                          rows={2}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Visual Direction</span>
                        <textarea
                          value={scene.visual_direction || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "visual_direction", event.target.value)
                          }
                          rows={3}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Animation Direction</span>
                        <textarea
                          value={scene.animation_direction || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "animation_direction", event.target.value)
                          }
                          rows={3}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <div className="space-y-3 rounded-2xl border border-[#ecd2df] bg-white p-4 md:col-span-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-black text-[#4d3040]">Copy Slots</p>
                          <button
                            type="button"
                            onClick={() => addV2CopySlot(sceneIndex)}
                            className="rounded-full bg-[#fff1f8] px-3 py-1.5 text-xs font-bold text-[#a33770] transition-colors hover:bg-[#ff0099] hover:text-white"
                          >
                            Add copy
                          </button>
                        </div>
                        {Object.entries(scene.copy_slots || {}).length ? (
                          Object.entries(scene.copy_slots || {}).map(([key, value]) => (
                            <div key={key} className="grid gap-2 md:grid-cols-[220px_minmax(0,1fr)_auto]">
                              <input
                                value={key}
                                readOnly
                                className="rounded-xl border border-[#ecd2df] bg-[#fff8fb] px-3 py-2 text-xs font-bold text-[#735f6d] outline-none"
                              />
                              <input
                                value={value || ""}
                                onChange={(event) => updateV2CopySlot(sceneIndex, key, event.target.value)}
                                className="rounded-xl border border-[#ecd2df] px-3 py-2 text-xs outline-none focus:border-[#ff0099]"
                              />
                              <button
                                type="button"
                                onClick={() => removeV2CopySlot(sceneIndex, key)}
                                className="rounded-xl border border-[#ecd2df] bg-white px-3 py-2 text-xs font-bold text-[#735f6d] transition-colors hover:border-red-300 hover:text-red-600"
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs leading-5 text-[#8d6d7f]">
                            No copy slots yet. Add editable copy that AI should place as Polotno text.
                          </p>
                        )}
                      </div>

                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Asset Roles</span>
                        <input
                          value={tagsToText(scene.asset_roles)}
                          onChange={(event) =>
                            updateV2SceneListField(sceneIndex, "asset_roles", event.target.value)
                          }
                          placeholder="background, hero_visual, decorative_overlay"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Asset Usage Notes</span>
                        <textarea
                          value={scene.asset_usage_notes || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "asset_usage_notes", event.target.value)
                          }
                          rows={2}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Transition Direction</span>
                        <input
                          value={scene.transition_direction || ""}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "transition_direction", event.target.value)
                          }
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Readability</span>
                        <select
                          value={scene.readability_level || "medium"}
                          onChange={(event) =>
                            updateV2SceneField(sceneIndex, "readability_level", event.target.value)
                          }
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Must Include</span>
                        <textarea
                          value={tagsToText(scene.must_include)}
                          onChange={(event) =>
                            updateV2SceneListField(sceneIndex, "must_include", event.target.value)
                          }
                          rows={2}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Must Avoid</span>
                        <textarea
                          value={tagsToText(scene.must_avoid)}
                          onChange={(event) =>
                            updateV2SceneListField(sceneIndex, "must_avoid", event.target.value)
                          }
                          rows={2}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                    </div>
                  </details>
                ))}
              </div>
            ) : (
              <div className="mt-5 rounded-[24px] border border-dashed border-[#ecd2df] bg-[#fff8fb] p-5 text-sm leading-6 text-[#735f6d]">
                Reset to family defaults to create editable scene cards for this template family.
              </div>
            )}
          </details>

          <details className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-black text-[#4d3040]">Advanced V2 asset brief</p>
                  <p className="mt-1 text-xs leading-5 text-[#735f6d]">
                    Optional legacy asset requirements. V3 Scene Asset Mapping controls the draft when a V3 plan exists.
                  </p>
                </div>
                <span className="w-fit rounded-full bg-[#fff8fb] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#7f2556]">
                  Advanced
                </span>
              </div>
            </summary>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                  <PencilRuler size={16} />
                  Advanced V2 asset brief
                </div>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  Smart-default asset requirements for creating/reviewing library assets. These
                  prompts do not override V3 Scene Asset Mapping when generating a V3 draft.
                </p>
              </div>
              {form.v2_brief ? (
                <button
                  type="button"
                  onClick={addV2Asset}
                  className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#251a24] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099]"
                >
                  Add asset
                </button>
              ) : (
                <button
                  type="button"
                  onClick={resetToFamilyDefaults}
                  className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#251a24] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099]"
                >
                  Reset to Family Defaults
                </button>
              )}
            </div>

            {form.v2_brief ? (
              <div className="mt-5 space-y-4">
                <div className="rounded-[24px] border border-[#ecd2df] bg-[#fffafd] p-4">
                  <label className="space-y-2">
                    <span className="text-sm font-bold text-[#4d3040]">Asset Plan Mode</span>
                    <select
                      value={form.v2_brief.asset_plan?.mode || "mixed"}
                      onChange={(event) => updateV2AssetPlanField("mode", event.target.value)}
                      className="w-full rounded-2xl border border-[#ecd2df] bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                    >
                      <option value="mixed">Mixed: reuse, reference, and generate</option>
                      <option value="generate_assets">Generate new assets</option>
                      <option value="generate_prompts_only">Generate prompts only</option>
                      <option value="reuse_existing_assets">Reuse existing assets</option>
                    </select>
                  </label>
                  <p className="mt-3 text-xs leading-5 text-[#735f6d]">
                    This controls the default AI strategy. Individual assets can still override it
                    with their own source strategy and usage mode.
                  </p>
                </div>

                {form.v2_brief.asset_plan?.assets?.length ? (
                  form.v2_brief.asset_plan.assets.map((asset, assetIndex) => (
                    <details
                      key={`${asset.asset_key || asset.role || "asset"}-${assetIndex}`}
                      open={assetIndex === 0}
                      className="rounded-[26px] border border-[#ecd2df] bg-[#fffafd] p-4"
                    >
                      <summary className="cursor-pointer list-none">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#a66f8c]">
                              {asset.source_strategy?.replace(/_/g, " ") || "asset"} - {asset.aspect_ratio || "asset native"}
                            </p>
                            <h3 className="mt-1 text-xl font-black text-[#251a24]">
                              {prettifyToken(
                                asset.role || asset.asset_key || `Asset ${assetIndex + 1}`
                              )}
                            </h3>
                            <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                              {asset.purpose || "Describe what this asset should do in the template."}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {asset.transparent_background_required ? (
                              <span className="rounded-full bg-[#eef8f1] px-3 py-1.5 text-xs font-bold text-[#277545]">
                                transparent
                              </span>
                            ) : null}
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                removeV2Asset(assetIndex);
                              }}
                              className="rounded-full border border-[#ecd2df] bg-white px-3 py-1.5 text-xs font-bold text-[#735f6d] transition-colors hover:border-red-300 hover:text-red-600"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </summary>

                      <div className="mt-5 grid gap-4 md:grid-cols-2">
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Asset Key</span>
                          <input
                            value={asset.asset_key || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "asset_key", event.target.value)
                            }
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Role</span>
                          <input
                            value={asset.role || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "role", event.target.value)
                            }
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <label className="space-y-2 md:col-span-2">
                          <span className="text-sm font-bold text-[#4d3040]">Purpose</span>
                          <textarea
                            value={asset.purpose || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "purpose", event.target.value)
                            }
                            rows={2}
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Source Strategy</span>
                          <select
                            value={asset.source_strategy || "generate_new_asset"}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "source_strategy", event.target.value)
                            }
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          >
                            <option value="use_library_asset">Use library asset</option>
                            <option value="generate_new_asset">Generate new asset</option>
                            <option value="reference_library_asset">Reference library asset</option>
                            <option value="transform_library_asset">Transform library asset</option>
                            <option value="prompt_only">Prompt only</option>
                          </select>
                        </label>
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Usage Mode</span>
                          <select
                            value={asset.usage_mode || "place_directly"}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "usage_mode", event.target.value)
                            }
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          >
                            <option value="place_directly">Place directly</option>
                            <option value="reference_only">Reference only</option>
                            <option value="transform">Transform</option>
                            <option value="style_match">Style match</option>
                          </select>
                        </label>
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Target Scenes</span>
                          <input
                            value={tagsToText(asset.target_scenes)}
                            onChange={(event) =>
                              updateV2AssetListField(assetIndex, "target_scenes", event.target.value)
                            }
                            placeholder="opening_blessing, closing_blessing"
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <label className="space-y-2">
                          <span className="text-sm font-bold text-[#4d3040]">Aspect Ratio</span>
                          <input
                            value={asset.aspect_ratio || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "aspect_ratio", event.target.value)
                            }
                            placeholder="9:16, asset_native, wide_overlay"
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <div className="grid gap-3 md:grid-cols-2">
                          <label className="space-y-2">
                            <span className="text-sm font-bold text-[#4d3040]">Intended Width</span>
                            <input
                              type="number"
                              value={asset.intended_width || ""}
                              onChange={(event) =>
                                updateV2AssetField(
                                  assetIndex,
                                  "intended_width",
                                  event.target.value ? Number(event.target.value) : null
                                )
                              }
                              className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                            />
                          </label>
                          <label className="space-y-2">
                            <span className="text-sm font-bold text-[#4d3040]">Intended Height</span>
                            <input
                              type="number"
                              value={asset.intended_height || ""}
                              onChange={(event) =>
                                updateV2AssetField(
                                  assetIndex,
                                  "intended_height",
                                  event.target.value ? Number(event.target.value) : null
                                )
                              }
                              className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                            />
                          </label>
                        </div>
                        <label className="flex items-center gap-3 rounded-2xl border border-[#ecd2df] bg-white px-4 py-3 text-sm font-bold text-[#4d3040]">
                          <input
                            type="checkbox"
                            checked={Boolean(asset.transparent_background_required)}
                            onChange={(event) =>
                              updateV2AssetField(
                                assetIndex,
                                "transparent_background_required",
                                event.target.checked
                              )
                            }
                            className="h-4 w-4 accent-[#ff0099]"
                          />
                          Transparent background required
                        </label>
                        <label className="space-y-2 md:col-span-2">
                          <span className="text-sm font-bold text-[#4d3040]">Prompt</span>
                          <textarea
                            value={asset.prompt || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "prompt", event.target.value)
                            }
                            rows={4}
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        <label className="space-y-2 md:col-span-2">
                          <span className="text-sm font-bold text-[#4d3040]">Negative Prompt</span>
                          <textarea
                            value={asset.negative_prompt || ""}
                            onChange={(event) =>
                              updateV2AssetField(assetIndex, "negative_prompt", event.target.value)
                            }
                            rows={3}
                            className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                          />
                        </label>
                        {asset.aspect_ratio === "9:16" &&
                        /ganesha|flower|garland|diya|rangoli|text/i.test(asset.prompt || "") ? (
                          <div className="md:col-span-2 rounded-2xl border border-[#f1d8a4] bg-[#fff9e9] px-4 py-3 text-sm leading-6 text-[#7c5412]">
                            This full-screen prompt mentions movable/decorative objects. If this is a
                            static background, keep Ganesha, flowers, garlands, diya, rangoli, and
                            text as separate assets.
                          </div>
                        ) : null}
                      </div>
                    </details>
                  ))
                ) : (
                  <div className="rounded-[24px] border border-dashed border-[#ecd2df] bg-[#fff8fb] p-5 text-sm leading-6 text-[#735f6d]">
                    No advanced assets yet. Add an asset or reset to family defaults.
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 rounded-[24px] border border-dashed border-[#ecd2df] bg-[#fff8fb] p-5 text-sm leading-6 text-[#735f6d]">
                Reset to family defaults to create editable advanced asset requirements.
              </div>
            )}
          </details>

          <details className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <summary className="cursor-pointer list-none">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm font-black text-[#4d3040]">Global template bindings</p>
                  <p className="mt-1 text-xs leading-5 text-[#735f6d]">
                    Internal summary of the role-to-asset bindings that will be persisted with
                    this draft. Edit scene-specific choices in Scene Asset Mapping above.
                  </p>
                </div>
                <span className="w-fit rounded-full bg-[#fff8fb] px-3 py-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#7f2556]">
                  {getTemplateAssetBindings(form).length} bindings
                </span>
              </div>
            </summary>

            <div className="mt-5 space-y-3">
              {getTemplateAssetBindings(form).length ? (
                getTemplateAssetBindings(form).map((binding) => {
                  const libraryAsset = assetLibraryItems.find(
                    (asset) => String(asset.id) === String(binding.asset_library_item_id)
                  );
                  const previewUrl = getAssetPreviewUrl(libraryAsset) || binding.preview_url || binding.media_url || "";

                  return (
                    <div
                      key={`${binding.binding_scope || "template_role"}-${binding.slot_key || binding.role}-${binding.asset_library_item_id}`}
                      className="flex gap-3 rounded-[22px] border border-[#f0dde8] bg-[#fffafd] p-3"
                    >
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-[#ecd2df] bg-white">
                        {previewUrl ? (
                          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-[#a66f8c]">
                            Asset
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-black text-[#341f2d]">{prettifyToken(binding.role)}</p>
                        <p className="mt-1 truncate text-xs text-[#735f6d]">
                          {libraryAsset?.name || binding.asset_name || `Asset ${binding.asset_library_item_id}`}
                        </p>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-[#a66f8c]">
                          {binding.binding_scope === "scene_slot"
                            ? binding.scene_name || binding.scene_id || "scene slot"
                            : "global role"}{" "}
                          / {binding.usage_mode || "place_directly"}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="rounded-2xl border border-dashed border-[#ecd2df] bg-[#fff8fb] px-4 py-4 text-sm leading-6 text-[#735f6d]">
                  No global bindings yet. Select assets in Scene Asset Mapping to create the
                  bindings used by draft generation.
                </p>
              )}
            </div>
          </details>

          <section className="rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                  <PencilRuler size={16} />
                  Need more assets?
                </div>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  Keep template creation focused here. Use Asset Library to upload, generate,
                  review, and organize reusable assets, then return to map them into scenes.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/admin/assets")}
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#251a24] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099]"
              >
                Open Asset Library
              </button>
            </div>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl bg-[#eef8f1] px-4 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#277545]">
                  Approved usable
                </p>
                <p className="mt-1 text-lg font-black text-[#1f5f38]">{approvedUsableAssets.length}</p>
              </div>
              <div className="rounded-2xl bg-[#fff1f8] px-4 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#a33770]">
                  Current bindings
                </p>
                <p className="mt-1 text-lg font-black text-[#7f2556]">
                  {getTemplateAssetBindings(form).length}
                </p>
              </div>
              <div className="rounded-2xl bg-[#fff9e9] px-4 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#9a6a10]">
                  Unresolved suggestions
                </p>
                <p className="mt-1 text-lg font-black text-[#7c5412]">
                  {unresolvedAssetRequirements.length}
                </p>
              </div>
            </div>
          </section>
        </div>

        <aside className="h-fit rounded-[32px] border border-[#efd9e4] bg-white p-5 shadow-sm xl:sticky xl:top-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
            Review
          </p>
          <h2 className="mt-2 text-2xl font-black text-[#251a24]">Draft summary</h2>
          <div className="mt-5 space-y-4 text-sm text-[#735f6d]">
            <div>
              <p className="font-bold text-[#4d3040]">Template</p>
              <p>{form.template_name || "Untitled draft"}</p>
            </div>
            <div>
              <p className="font-bold text-[#4d3040]">Preset</p>
              <p>{activeFamily.stylePresets.find((preset) => preset.id === form.style_preset_id)?.label}</p>
            </div>
            <div>
              <p className="font-bold text-[#4d3040]">Palette</p>
              <div className="mt-2 flex gap-2">
                {Object.values(getCreativeBriefPalette(form)).map((color) => (
                  <span
                    key={color}
                    className="h-8 w-8 rounded-full border border-[#ecd2df]"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
            <div>
              <p className="font-bold text-[#4d3040]">Template Bindings</p>
              <p>{getTemplateAssetBindings(form).length} role bindings for this draft</p>
              <p className="mt-1 text-xs leading-5">
                {(form.selected_assets || []).length} broader selected asset reference
                {(form.selected_assets || []).length === 1 ? "" : "s"}
              </p>
            </div>
            <div>
              <p className="font-bold text-[#4d3040]">Asset Suggestions</p>
              <p>
                {assetReadiness?.score === 100 && !unresolvedAssetRequirements.length
                  ? "All required roles satisfied"
                  : unresolvedAssetRequirements.length
                  ? `${unresolvedAssetRequirements.length} unresolved suggestion${unresolvedAssetRequirements.length === 1 ? "" : "s"}`
                  : form.asset_creation_mode === "generate_all"
                  ? `Can suggest ${activeFamily.generatableAssetRoles.length} reusable roles`
                  : "No unresolved suggestions"}
              </p>
            </div>
            {assetReadiness ? (
              <div
                className={`rounded-[22px] border p-4 ${
                  assetReadiness.state === "premium_ready"
                    ? "border-[#cfe9d8] bg-[#f4fbf6] text-[#25733f]"
                    : assetReadiness.state === "starter_ready"
                    ? "border-[#f1d8a4] bg-[#fff9e9] text-[#7c5412]"
                    : "border-[#f1c4c4] bg-[#fff6f6] text-[#8a2d2d]"
                }`}
              >
                <p className="font-bold">Asset readiness</p>
                <p className="mt-1 text-sm">
                  {assetReadiness.score}/100 - {assetReadiness.state.replace(/_/g, " ")}
                </p>
                <p className="mt-2 text-xs leading-5">
                  {assetReadiness.missingRequired.length} required missing,{" "}
                  {assetReadiness.missingRecommended.length} recommended missing.
                </p>
              </div>
            ) : null}
            {form.v2_brief ? (
              <div className="rounded-[22px] border border-[#dbeafe] bg-[#eff6ff] p-4 text-[#1d4d86]">
                <p className="font-bold text-[#153d6d]">Template Setup Brief</p>
                <p className="mt-1">
                  {v2BriefSummary.sceneCount} scenes, {v2BriefSummary.assetCount} planned assets
                </p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em]">
                  {v2BriefSummary.motionTarget.replace(/_/g, " ")}
                </p>
                {form.defaults_sources?.length ? (
                  <p className="mt-2 text-xs leading-5">
                    Defaults: {form.defaults_sources.join(" > ")}
                  </p>
                ) : null}
                {form.defaults_warnings?.length ? (
                  <p className="mt-2 text-xs leading-5 text-[#8a5a10]">
                    {form.defaults_warnings[0]}
                  </p>
                ) : null}
                <div className="mt-3 max-h-52 space-y-2 overflow-y-auto pr-1">
                  {(form.v2_brief.scene_briefs || []).map((scene, index) => (
                    <div key={scene.scene_id || index} className="rounded-2xl bg-white/75 px-3 py-2">
                      <p className="text-xs font-black text-[#153d6d]">
                        {index + 1}. {scene.scene_name}
                      </p>
                      <p className="mt-1 text-[11px] leading-4 text-[#4f6f9b]">
                        {scene.animation_direction || scene.visual_direction}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-[#4f6f9b]">
                  Recommended flow: generate a composition plan first. With a plan active,
                  Generate Draft uses the composition plus Scene Asset Mapping. This brief is
                  only setup context.
                </p>
              </div>
            ) : (
              <div className="rounded-[22px] border border-[#ecd2df] bg-[#fff8fb] p-4">
                <p className="font-bold text-[#4d3040]">Template Setup Defaults</p>
                <p className="mt-1 text-sm leading-6">
                  Load default scene briefs, asset plans, and motion intent for the selected
                  template family.
                </p>
              </div>
            )}
            {compositionPlan ? (
              <div className="rounded-[22px] border border-[#c7dff7] bg-white p-4 text-[#1d4d86]">
                <p className="font-bold text-[#153d6d]">V3 Composition</p>
                <p className="mt-1">
                  {compositionScenes.length} scenes,{" "}
                  {compositionScenes.reduce(
                    (total, scene) => total + (scene.elements?.length || 0),
                    0
                  )}{" "}
                  elements
                </p>
                <p className="mt-2 text-xs leading-5">
                  Source: {compositionResult?.source || "planner"}
                  {getTemplateAssetBindings(form).length
                    ? `, ${getTemplateAssetBindings(form).length} current role bindings`
                    : ", no current role bindings"}
                  {compositionMissingRoles.length
                    ? `, ${compositionMissingRoles.length} unresolved roles`
                    : ", no unresolved roles"}
                </p>
                {compositionQuality ? (
                  <p className="mt-2 text-xs font-bold leading-5">
                    Quality: {compositionQuality.score}/100 -{" "}
                    {compositionQuality.status.replace(/_/g, " ")}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {unresolvedAssetRequirements.length ? (
            <div className="mt-6 rounded-[24px] border border-[#f1d8a4] bg-[#fff9e9] p-4">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#9a6a10]">
                Unresolved asset suggestions
              </p>
              <div className="mt-3 space-y-3">
                {unresolvedAssetRequirements.slice(0, 6).map((requirement, index) => {
                  const requirementKey = requirement.id || `${getRequirementRole(requirement)}-${index}`;
                  const isCreated = createdRequirementKeys.has(requirementKey);

                  return (
                    <div
                      key={requirementKey}
                      className="rounded-2xl border border-[#efd9b4] bg-white/70 px-3 py-3"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-black text-[#3f2d1d]">
                            {getRequirementRole(requirement)}
                          </p>
                          <p className="mt-1 text-xs leading-5 text-[#755c37]">
                            {requirement.description || requirement.generation_prompt || "Review in asset library workflow."}
                          </p>
                          {[getRequirementGroup(requirement), getRequirementType(requirement), requirement.priority]
                            .filter(Boolean)
                            .length ? (
                            <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#a77a23]">
                              {[getRequirementGroup(requirement), getRequirementType(requirement), requirement.priority]
                                .filter(Boolean)
                                .join(" | ")}
                            </p>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => createAssetBrief(requirement)}
                          disabled={isCreated || creatingRequirementKey === requirementKey}
                          className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#3f2d1d] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isCreated
                            ? "Brief created"
                            : creatingRequirementKey === requirementKey
                            ? "Creating..."
                            : "Create asset brief"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-xs leading-5 text-[#755c37]">
                These are non-blocking suggestions from the planner. Create them only if you want
                richer variants or extra reusable library assets.
              </p>
              <button
                type="button"
                onClick={openAssetLibraryWithRequirements}
                className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-[#d9a84d] bg-white px-4 py-2.5 text-xs font-bold text-[#7c5412] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
              >
                Open asset library
              </button>
            </div>
          ) : null}

          <div className="mt-6 grid gap-3">
            <button
              type="button"
              onClick={() => updateField("is_featured", !form.is_featured)}
              className={`flex items-center justify-between rounded-[22px] border px-4 py-3 text-left transition-all ${
                form.is_featured ? "border-[#ffcc57] bg-[#fff7dc]" : "border-[#ecd2df] bg-white"
              }`}
            >
              <span className="text-sm font-bold text-[#4d3040]">Featured</span>
              <Star className={`h-5 w-5 ${form.is_featured ? "text-[#cc8a00]" : "text-[#d7b46c]"}`} />
            </button>
            <button
              type="button"
              onClick={() => updateField("is_premium", !form.is_premium)}
              className={`flex items-center justify-between rounded-[22px] border px-4 py-3 text-left transition-all ${
                form.is_premium ? "border-[#d9b5ff] bg-[#faf2ff]" : "border-[#ecd2df] bg-white"
              }`}
            >
              <span className="text-sm font-bold text-[#4d3040]">Premium</span>
              <Crown className={`h-5 w-5 ${form.is_premium ? "text-[#8c46d8]" : "text-[#b48cd7]"}`} />
            </button>
          </div>

          <div className="mt-6 rounded-[24px] border border-[#efd9e4] bg-[#fffafd] p-4">
            <p className="text-sm font-black text-[#251a24]">Draft readiness checklist</p>
            <p className="mt-1 text-xs leading-5 text-[#735f6d]">
              This reflects the V3 draft path. Template defaults are only fallback/context when no V3 plan exists.
            </p>
            <div className="mt-3 space-y-2">
              {draftReadinessItems.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-2xl border px-3 py-3 ${
                    item.ready ? "border-[#cfe9d8] bg-[#f4fbf6]" : "border-[#f1d8a4] bg-[#fff9e9]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className={`text-xs font-black ${item.ready ? "text-[#25733f]" : "text-[#7c5412]"}`}>
                        {item.label}
                      </p>
                      <p className="mt-1 text-[11px] leading-4 text-[#735f6d]">{item.detail}</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] ${
                        item.ready ? "bg-white text-[#25733f]" : "bg-white text-[#7c5412]"
                      }`}
                    >
                      {item.ready ? "Ready" : "Check"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={isGenerating}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? "Building draft..." : createdDraft ? "Rebuild Local Draft" : "Build Local Draft"}
          </button>
          {statusMessage ? (
            <div
              className={`mt-3 rounded-2xl border px-4 py-3 text-sm font-semibold ${
                statusTone === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : statusTone === "success"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-[#efd9e4] bg-white text-[#735f6d]"
              }`}
            >
              {statusMessage}
            </div>
          ) : null}
          {createdDraft ? (
            <button
              type="button"
              onClick={openCreatedDraft}
              className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#251a24] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099]"
            >
              Open Polotno
              <ArrowRight size={16} />
            </button>
          ) : null}
        </aside>
      </main>
    </div>
  );
}

export default AdminAiTemplateCreatePage;
